import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet, Save,
  ArrowUp, ArrowDown, CheckSquare
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useVirtualizer } from "@tanstack/react-virtual";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatUSD2 } from "../../utils/format";
import { useTipoCambio, fmtTC } from "../../utils/tipoCambio";   // Fix FG (2026-10-08)
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import { ItemRow } from "./ItemRow";
import { FinancialsTab } from "./FinancialsTab";
import { ImportPreviewModal } from "./ImportPreviewModal";
import { BocasTab } from "./BocasTab";
import { ManoObraTab } from "./ManoObraTab";
import { CatalogosExtTab } from "./CatalogosExtTab";
import { TablerosTab } from "./TablerosTab";
import { RevisionsModal } from "./RevisionsModal";
import { MarcasCotizadasSplitField } from "./MarcasCotizadasSplitField";
import { NivelesField } from "./NivelesField";
import { ImportTareasDesgloseModal } from "./ImportTareasDesgloseModal";
import { AsignarTareasModal } from "./AsignarTareasModal";
import { AsignarNivelesModal } from "./AsignarNivelesModal";
import { AutollenadoIAModal } from "./AutollenadoIAModal";
import { CatalogoPresupuestoContext, PRESUPUESTO_SIN_GUARDAR } from "./catalogoScope";
import { computeBudgetLive, computeItemNumbers } from "./budgetCalcs";
import {
  expandSelectionWithChildren, countOrphanItems,
  moveSelectedBlockToTarget, moveSelectedBlockStep,
} from "./selectionUtils";
import ClienteFormModal from "../../components/ClienteFormModal";
import ObraFormModal from "../../components/ObraFormModal";
import AdicionalObraModal from "./AdicionalObraModal";

// FIX 1 — Estados válidos para cambio manual (alineados al backend)
const MANUAL_STATUS_OPTIONS = [
  { key: "recepcion", label: "Recepción" },
  { key: "computo", label: "Cómputo" },
  { key: "cotizacion", label: "Cotización" },
  { key: "revision", label: "Revisión" },
  { key: "enviado", label: "Enviado" },
  { key: "aprobado", label: "Aprobado" },
  { key: "rechazado", label: "Rechazado" },
];
const MANUAL_STATUS_ROLES = ["Administrador", "Superadministrador", "Gerencia Técnica", "Gerente Tecnico"];
const manualStatusLabel = (k) => (MANUAL_STATUS_OPTIONS.find(s => s.key === k) || {}).label || k;

export function BudgetForm({ budget, parentBudget, projects, clientes, obras, onSave, onClose, onBudgetUpdated }) {
  const { user } = useAuth();
  const isProyectista = user?.role === "Proyectista";
  const initPct = budget?.porcentajes || parentBudget?.porcentajes || DEFAULT_PCT;
  const userInitials = (user?.name || "").split(" ").map(p => p[0]).slice(0, 3).join("").toUpperCase();
  const [form, setForm] = useState({
    nombre: budget?.nombre || (parentBudget ? `Adicional - ${parentBudget.nombre}` : ""),
    tipo: budget?.tipo || parentBudget?.tipo || "obra",
    cliente: budget?.cliente || parentBudget?.cliente || "",
    cliente_id: budget?.cliente_id || parentBudget?.cliente_id || "",
    cliente_codigo: budget?.cliente_codigo || parentBudget?.cliente_codigo || "",
    ingeniero_contacto: budget?.ingeniero_contacto || parentBudget?.ingeniero_contacto || "",
    obra: budget?.obra || parentBudget?.obra || "",
    obra_id: budget?.obra_id || parentBudget?.obra_id || "",
    project_id: budget?.project_id || parentBudget?.project_id || "",
    deadline: budget?.deadline || "",
    fecha_elaboracion: budget?.fecha_elaboracion || new Date().toISOString().slice(0, 10),
    elaborator_initials: budget?.elaborator_initials || userInitials,
    is_adicional: budget?.is_adicional || !!parentBudget,
    parent_budget_id: budget?.parent_budget_id || parentBudget?._id || "",
    condicion_pago: budget?.condicion_pago || "",
    // Fix FG (2026-10-08): moneda de la cotización y su tipo de cambio.
    moneda: budget?.moneda || "PYG",
    tipo_cambio: budget?.tipo_cambio ?? null,
    marcas_cotizadas: budget?.marcas_cotizadas || "",
    // C.2 — Split de marcas cotizadas por categoría (6 keys fijas)
    marcas_cotizadas_split: budget?.marcas_cotizadas_split || null,
    alcance_incluye: budget?.alcance_incluye || "",
    alcance_no_incluye: budget?.alcance_no_incluye || "",
    obs_tecnicas: budget?.obs_tecnicas || "",
    // Fix DI (2026-09-17): en un presupuesto NUEVO estos cuatro campos se
    // completan solos con los textos por defecto de Configuración (efecto más
    // abajo). En uno existente se respeta lo que tenga guardado, incluso vacío.
    // V07: Firma configurable
    firma_nombre: budget?.firma_nombre || "",
    firma_cargo: budget?.firma_cargo || "",
    // CEN 2.0 FASE 3 — niveles físicos de la obra
    niveles: budget?.niveles || [],
    // CEN 2.0 FASE 2 sub-entrega 4 — tipo de instalación
    tipo_instalacion: budget?.tipo_instalacion || "",
  });
  const [pct, setPct] = useState({ ganancia_mat_pct: initPct.ganancia_mat_pct || 10, ga_pct: initPct.ga_pct || 10, gmo_pct: initPct.gmo_pct || 45 });
  // Descuento (Feb 2026)
  const initDesc = budget?.descuento || { activo: false, porcentaje: 0, aplica_sobre: "utilidades" };
  const [descuento, setDescuento] = useState({
    activo: !!initDesc.activo,
    porcentaje: parseFloat(initDesc.porcentaje || 0),
    aplica_sobre: initDesc.aplica_sobre || "utilidades",
  });
  const [descFinalTxt, setDescFinalTxt] = useState(null);   // Fix HW: lo que se está escribiendo en «Descuento final sobre el total»
  // Pedido video 2 #4 — modo de captura de precios (alineado a planilla Excel CEN).
  // Default nuevos: "con_iva". Legacy: lo que tenga el doc en BD.
  const [precioInputMode, setPrecioInputMode] = useState(
    budget?.precio_input_mode || (budget?._id ? "sin_iva" : "con_iva")
  );
  // PDF REV3 #7 — items necesitan un id estable para drag-and-drop
  const makeItemId = () => `i_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  // CEN 2.0 FASE 1 (CIMIENTOS) — si el ítem ya tiene `item_uid` (persistido por el
  // backend), se usa como __id: sobrevive a reload/guardado, no solo a la sesión.
  const ensureIds = (arr) => (arr || []).map((it) => (it && it.__id ? it : { ...(it || {}), __id: (it && it.item_uid) || makeItemId() }));
  // BLOQUE 61 — inicializador PEREZOSO (función, no valor directo). Antes
  // `useState(ensureIds(...))` recalculaba `ensureIds` sobre TODOS los ítems en
  // CADA render (React descarta el resultado salvo en el montaje, pero igual lo
  // computa) — con ~1.830 ítems eso significaba recrear 1.830 objetos por cada
  // re-render sin ningún uso, sumándose a cualquier otra causa de re-render.
  const [items, setItems] = useState(() => ensureIds(budget?.items?.length > 0 ? budget.items : [
    { numero: "1.0", descripcion: "PUESTO DE TRANSFORMACIÓN", unidad: "glo", cantidad: 0, costo_unit_mat: 0, costo_unit_mo: 0, is_group: true },
    { numero: "1.1", descripcion: "", unidad: "un", cantidad: 1, costo_unit_mat: 0, costo_unit_mo: 0, is_group: false },
  ]));
  // BLOQUE 59 — Selección múltiple de filas. Vive en el padre (indexada por __id
  // estable), NUNCA en la fila: con la virtualización del Bloque 54 las filas se
  // desmontan al salir de la vista, así que el estado de selección se perdería si
  // viviera en ItemRow. lastCheckedIdxRef es el ancla para el rango de Shift+Click.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionAutoIncluded, setSelectionAutoIncluded] = useState(false);
  const lastCheckedIdxRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // PDF REV3 #4 — Estado del borrador (timestamp del último guardado parcial)
  const [lastDraftAt, setLastDraftAt] = useState(budget?.last_draft_saved_at || null);
  // PDF REV3 #5 + #6 — Modal de revisiones
  const [showRevisionsModal, setShowRevisionsModal] = useState(false);
  // CEN 2.0 FASE 2 sub-entrega 2 — Tareas/Niveles asistidos por ítem
  const [showImportTareas, setShowImportTareas] = useState(false);
  const [showAsignarTareas, setShowAsignarTareas] = useState(false);
  const [showAsignarNiveles, setShowAsignarNiveles] = useState(false);
  const [showAutollenado, setShowAutollenado] = useState(false);
  const patchItemsField = useCallback((byUid, field) => {
    setItems(prev => prev.map(it => (it.item_uid && byUid[it.item_uid] !== undefined) ? { ...it, [field]: byUid[it.item_uid] } : it));
  }, []);
  const replaceItemsFromServer = useCallback((serverItems) => {
    setItems(ensureIds(serverItems));
  }, []);
  // CEN 2.0 FASE 2 sub-entrega 4 — catálogo editable de tipo_instalacion
  const [tiposInstalacion, setTiposInstalacion] = useState([]);
  useEffect(() => {
    axios.get(`${API}/settings/tipos-instalacion`, opts).then(({ data }) => setTiposInstalacion(data.tipos || [])).catch(() => {});
  }, []);
  // Fix DI (2026-09-17): en un presupuesto NUEVO los cuatro textos de Alcance /
  // Observaciones se completan con los de Configuración. Solo al abrir el
  // formulario y solo sobre el campo que esté vacío, así no pisa nada de lo que
  // el usuario ya escribió ni lo que traiga un presupuesto existente. Si no hay
  // textos cargados o el pedido falla, el formulario abre como hasta ahora.
  useEffect(() => {
    if (budget?._id) return;
    axios.get(`${API}/settings/textos-presupuesto`, opts)
      .then(({ data }) => {
        setForm(f => ({
          ...f,
          alcance_incluye: f.alcance_incluye || data.alcance_incluye || "",
          alcance_no_incluye: f.alcance_no_incluye || data.alcance_no_incluye || "",
          obs_tecnicas: f.obs_tecnicas || data.obs_tecnicas || "",
          condicion_pago: f.condicion_pago || data.condicion_pago || "",
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // PDF REV3 #2 + #3 — Tracking: si los items vinieron de Excel, marcar cómputo disponible
  const [computoFromExcel, setComputoFromExcel] = useState(!!budget?.computo_disponible);
  const [computoSource, setComputoSource] = useState(budget?.computo_source || "");
  // A.1 — Estado actual del presupuesto (se actualiza con hidratar/transiciones)
  const [currentStatus, setCurrentStatus] = useState(budget?.status || "");
  // A.1 — Modal de confirmación antes de "Actualizar Presupuesto" (PUT → incrementa revisión)
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  // A.3.c — Modal de confirmación de transición de estado dentro del form
  const [showTransitionConfirm, setShowTransitionConfirm] = useState(null); // null | "revision" | "enviado" | "aprobado" | "rechazado"
  const [transitionComment, setTransitionComment] = useState("");
  const [transitionLoading, setTransitionLoading] = useState(false);
  // PROMPT 79 — Modal "Vincular Adicional a Obra" (al aprobar un budget is_adicional)
  const [showAdicionalModal, setShowAdicionalModal] = useState(false);
  const isAdicional = !!(form?.is_adicional || budget?.is_adicional);
  // Clientes creados inline desde el botón "+" del dropdown de cliente
  const [extraClientes, setExtraClientes] = useState([]);
  const [showNewCliente, setShowNewCliente] = useState(false);
  const clientesAll = useMemo(() => {
    const map = new Map();
    [...(clientes || []), ...extraClientes].forEach((c) => { if (c && c._id) map.set(c._id, c); });
    return Array.from(map.values());
  }, [clientes, extraClientes]);
  const handleClienteCreated = (nc) => {
    if (!nc || !nc._id) { setShowNewCliente(false); return; }
    setExtraClientes((prev) => [...prev.filter((c) => c._id !== nc._id), nc]);
    setForm((f) => ({ ...f, cliente_id: nc._id, cliente: nc.cliente, cliente_codigo: nc.codigo || "" }));
    setShowNewCliente(false);
  };
  // Obras creadas inline desde el botón "+ Nueva Obra" del dropdown de obra
  const [extraObras, setExtraObras] = useState([]);
  const [showNewObra, setShowNewObra] = useState(false);
  const obrasAll = useMemo(() => {
    const map = new Map();
    [...(obras || []), ...extraObras].forEach((o) => { if (o && o._id) map.set(o._id, o); });
    return Array.from(map.values());
  }, [obras, extraObras]);
  // Obras filtradas por el cliente seleccionado en el presupuesto
  const obrasDelCliente = useMemo(() => {
    if (!form.cliente_id) return obrasAll;
    return obrasAll.filter((o) => o.cliente_id === form.cliente_id || o._id === form.obra_id);
  }, [obrasAll, form.cliente_id, form.obra_id]);
  const handleObraCreated = (no) => {
    if (!no || !no._id) { setShowNewObra(false); return; }
    setExtraObras((prev) => [...prev.filter((o) => o._id !== no._id), no]);
    setForm((f) => ({ ...f, obra_id: no._id, obra: no.obra || f.obra }));
    setShowNewObra(false);
  };
  // FIX 1 — Cambio manual de estado (header del modal)
  const canChangeStatusManual = MANUAL_STATUS_ROLES.includes(user?.role);
  const [manualStatusTarget, setManualStatusTarget] = useState(null); // estado pendiente de confirmar
  const [manualStatusReason, setManualStatusReason] = useState("");
  const [manualStatusLoading, setManualStatusLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const onSuggBlurCb = useCallback(() => setTimeout(() => setSuggestions([]), 200), []);
  const [tab, setTab] = useState(budget?._id ? "items" : "datos");
  const searchTimer = useRef(null);

  // ── BUG FIX 19/05/2026 — Refresh fresco al abrir + re-hidratación ─────────
  // CAUSA RAÍZ #1: Budgets.jsx pasa el budget desde la lista local cacheada.
  // Si entre el último fetch y el "Editar" pasó un Save Draft o un cambio en
  // otra pestaña, el form arrancaba con datos STALE. Solución: al montar en
  // modo edición, hacemos GET fresco del budget y re-hidratamos el state
  // local antes de mostrar la UI.
  const [hydrating, setHydrating] = useState(!!budget?._id);

  // Helper: re-hidratar TODO el state local con un budget fresco del backend.
  // Se llama (a) al montar en modo edición, (b) tras save-draft, (c) tras
  // create-revision, (d) tras PUT exitoso. Garantiza que el form siempre
  // refleje el estado real de la DB.
  const hydrateFromBudget = useCallback((b) => {
    if (!b) return;
    // A.1 — Actualizar currentStatus cuando se re-hidrata el presupuesto
    setCurrentStatus(b.status || "");
    setForm({
      nombre: b.nombre || "",
      tipo: b.tipo || "obra",
      cliente: b.cliente || "",
      cliente_id: b.cliente_id || "",
      cliente_codigo: b.cliente_codigo || "",
      ingeniero_contacto: b.ingeniero_contacto || "",
      obra: b.obra || "",
      obra_id: b.obra_id || "",
      project_id: b.project_id || "",
      deadline: b.deadline || "",
      fecha_elaboracion: b.fecha_elaboracion || new Date().toISOString().slice(0, 10),
      elaborator_initials: b.elaborator_initials || userInitials,
      is_adicional: !!b.is_adicional,
      parent_budget_id: b.parent_budget_id || "",
      condicion_pago: b.condicion_pago || "",
      moneda: b.moneda || "PYG",   // Fix FG
      tipo_cambio: b.tipo_cambio ?? null,
      marcas_cotizadas: b.marcas_cotizadas || "",
      // C.2 — Split de marcas cotizadas por categoría (6 keys fijas)
      // BUG FIX: faltaba acá, causaba que al hacer "Guardar Avance" los 6 campos
      // del bloque "Marcas Cotizadas (por categoría)" se vaciaran visualmente.
      marcas_cotizadas_split: b.marcas_cotizadas_split || null,
      alcance_incluye: b.alcance_incluye || "",
      alcance_no_incluye: b.alcance_no_incluye || "",
      obs_tecnicas: b.obs_tecnicas || "",
      firma_nombre: b.firma_nombre || "",
      firma_cargo: b.firma_cargo || "",
      // CEN 2.0 FASE 3 — niveles físicos de la obra (BUG FIX: faltaba acá, causaba
      // que "Guardar Avance" borrara los niveles existentes y no se vieran al abrir).
      niveles: b.niveles || [],
      tipo_instalacion: b.tipo_instalacion || "",
    });
    const newPct = b.porcentajes || DEFAULT_PCT;
    setPct({
      ganancia_mat_pct: newPct.ganancia_mat_pct ?? 10,
      ga_pct: newPct.ga_pct ?? 10,
      gmo_pct: newPct.gmo_pct ?? 45,
    });
    setPrecioInputMode(b.precio_input_mode || "sin_iva");
    // Descuento hydration
    const bd = b.descuento || { activo: false, porcentaje: 0, aplica_sobre: "utilidades" };
    setDescuento({ activo: !!bd.activo, porcentaje: parseFloat(bd.porcentaje || 0), aplica_sobre: bd.aplica_sobre || "utilidades" });
    setItems(ensureIds(b.items?.length > 0 ? b.items : [
      { numero: "1.0", descripcion: "PUESTO DE TRANSFORMACIÓN", unidad: "glo", cantidad: 0, costo_unit_mat: 0, costo_unit_mo: 0, is_group: true },
      { numero: "1.1", descripcion: "", unidad: "un", cantidad: 1, costo_unit_mat: 0, costo_unit_mo: 0, is_group: false },
    ]));
    // BLOQUE 59 — al re-hidratar (post-guardado/GET fresco) los ítems reciben __id
    // nuevos: la selección anterior ya no correspondería a ninguna fila visible.
    setSelectedIds(new Set());
    setSelectionAutoIncluded(false);
    lastCheckedIdxRef.current = null;
    setLastDraftAt(b.last_draft_saved_at || null);
    setComputoFromExcel(!!b.computo_disponible);
    setComputoSource(b.computo_source || "");
  // Dependencias intencionalmente vacías (no recreamos el setter en cada render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GET fresco al montar (solo modo edición). Ojo: solo se ejecuta una vez
  // al montar el form. Si el padre re-renderiza con un budget._id distinto,
  // React reciclará el componente y este efecto correrá de nuevo.
  useEffect(() => {
    if (!budget?._id) {
      setHydrating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/budgets/${budget._id}`, opts);
        if (cancelled || !data) return;
        // Detectar si el padre nos pasó datos stale (delta de total > 1 Gs)
        const cachedTotal = Number(budget?.total || 0);
        const freshTotal = Number(data.total || 0);
        if (cachedTotal > 0 && Math.abs(freshTotal - cachedTotal) > 1) {
          toast.info(
            `Datos actualizados desde la base. Total: Gs. ${freshTotal.toLocaleString("es-PY")} (antes: ${cachedTotal.toLocaleString("es-PY")})`,
            { duration: 4000 }
          );
        }
        hydrateFromBudget(data);
        // Propagar al padre para que la lista deje de mostrar stale
        if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
      } catch (err) {
        // Si falla el GET fresco, NO bloqueamos el form, dejamos los datos
        // del prop pero mostramos advertencia. Mejor degradación parcial que
        // bloquear al usuario.
        const status = err?.response?.status;
        if (status === 404) {
          toast.error("Este presupuesto ya no existe en la base. Cerrá el formulario.");
        } else {
          toast.warning("No se pudo refrescar el presupuesto desde el servidor — trabajando con datos en caché. Recargá si ves inconsistencias.");
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  // Solo correr al montar con el _id del budget actual.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget?._id]);

  // PDF REV3 #8/#9/#10 — Anchos por defecto MÁS GENEROSOS para visibilizar 9-10 dígitos
  // (en numéricos: 96px era insuficiente para Gs. 999.999.999 = 11 chars + bordes)
  const COL_WIDTH_KEY = "cen-budget-col-widths-v2";
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COL_WIDTH_KEY) || "{}");
      return { desc: saved.desc || 340, num: saved.num || 64, cant: saved.cant || 88, ...saved };
    } catch (_e) {
      return { desc: 340, num: 64, cant: 88 };
    }
  });
  useEffect(() => {
    try { localStorage.setItem(COL_WIDTH_KEY, JSON.stringify(colWidths)); } catch (_e) { /* ignore */ }
  }, [colWidths]);

  const resizeRef = useRef(null);
  const startResize = useCallback((colKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[colKey] || 320;
    const onMove = (ev) => {
      const newW = Math.max(80, Math.min(1200, startW + (ev.clientX - startX)));
      setColWidths((prev) => ({ ...prev, [colKey]: newW }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [colWidths]);

  const handleSearch = useCallback((q, idx) => {
    setActiveIdx(idx);
    clearTimeout(searchTimer.current);
    if (q.length < 2) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/budgets/price-lists/search`, { params: { q }, ...opts });
        setSuggestions(res.data || []);
      } catch { setSuggestions([]); }
    }, 250);
  }, []);

  const updateItem = useCallback((idx, field, value) => {
    setItems(prev => {
      const copy = [...prev];
      const before = copy[idx];
      copy[idx] = { ...before, [field]: value };

      // Pedido video 4 #6: auto-propagate de precios cuando cambia costo unitario
      // y existen otros ítems con la EXACTA MISMA descripción.
      if ((field === "costo_unit_mat" || field === "costo_unit_mo") && !before.is_group) {
        const desc = (before.descripcion || "").trim().toLowerCase();
        if (desc.length >= 3) {
          let propagated = 0;
          for (let i = 0; i < copy.length; i++) {
            if (i === idx) continue;
            const it = copy[i];
            if (it.is_group) continue;
            const itDesc = (it.descripcion || "").trim().toLowerCase();
            if (itDesc === desc && Number(it[field] || 0) !== Number(value || 0)) {
              copy[i] = { ...it, [field]: value };
              propagated++;
            }
          }
          if (propagated > 0) {
            const which = field === "costo_unit_mat" ? "MAT" : "M.O";
            toast.success(`Precio ${which} replicado a ${propagated} ítem${propagated !== 1 ? "s" : ""} con la misma descripción`, { duration: 2500 });
          }
        }
      }
      return copy;
    });
  }, []);
  const addItem = useCallback(() => setItems(prev => [...prev, { __id: makeItemId(), numero: "", descripcion: "", unidad: "un", cantidad: 1, costo_unit_mat: 0, costo_unit_mo: 0, is_group: false }]), []);
  const addGroup = useCallback(() => setItems(prev => {
    // CEN Jun 2026 — "+ Agregar sección" auto-numera la nueva sección (2.0, 3.0, …).
    const majors = prev
      .filter(it => it.is_group && Number(it.nivel_jerarquia || 0) === 0)
      .map(it => parseInt(String(it.numero || "").split(".")[0], 10))
      .filter(n => !isNaN(n));
    const next = (majors.length ? Math.max(...majors) : 0) + 1;
    return [...prev, { __id: makeItemId(), numero: `${next}.0`, descripcion: "", unidad: "glo", cantidad: 0, costo_unit_mat: 0, costo_unit_mo: 0, is_group: true }];
  }), []);
  // FIX jerarquía — sub-sección (nivel 1): grupo visual anidado, sin valores.
  const addSubGroup = useCallback(() => setItems(prev => [...prev, { __id: makeItemId(), numero: "", descripcion: "", unidad: "glo", cantidad: 0, costo_unit_mat: 0, costo_unit_mo: 0, is_group: true, nivel_jerarquia: 1 }]), []);
  // FIX jerarquía (Jun 2026) — sub-sub-sección (nivel 2): encabezado de 3er nivel (ej. "8.1.1 ILUMINACION").
  const addSubSubGroup = useCallback(() => setItems(prev => [...prev, { __id: makeItemId(), numero: "", descripcion: "", unidad: "glo", cantidad: 0, costo_unit_mat: 0, costo_unit_mo: 0, is_group: true, nivel_jerarquia: 2 }]), []);
  // El botón "Agregar sub-sección" solo tiene sentido si ya hay una sección principal donde anidar.
  const hasMainGroup = items.some(it => it.is_group && Number(it.nivel_jerarquia || 0) === 0);
  // La sub-sub-sección requiere que ya exista al menos una sub-sección (nivel 1).
  const hasSubGroup = items.some(it => it.is_group && Number(it.nivel_jerarquia || 0) === 1);
  // PDF REV1 E.1: ítem especial "Materiales menores de montaje" — autocalculado como % del grupo.
  const addMatMenores = useCallback(() => setItems(prev => [...prev, {
    __id: makeItemId(),
    numero: "", descripcion: "Materiales menores de montaje", unidad: "glo", cantidad: 1,
    costo_unit_mat: 0, costo_unit_mo: 0, is_group: false,
    tipo_calculo: "menores_pct", menores_pct: 5,
  }]), []);
  const removeItem = useCallback((idx) => setItems(prev => {
    const removedId = prev[idx]?.__id;
    if (removedId) setSelectedIds((sel) => { if (!sel.has(removedId)) return sel; const next = new Set(sel); next.delete(removedId); return next; });
    return prev.filter((_, i) => i !== idx);
  }), []);

  // BLOQUE 59 — Selección múltiple: click en la casilla = toggle; Shift+Click = rango
  // desde la última marcada hasta la actual. Si la selección incluye una sección/
  // sub-sección, se auto-incluyen sus ítems hijos (subárbol completo) — aviso inline
  // vía `selectionAutoIncluded`, sin modal. `itemsRef` evita closures obsoletas dentro
  // del callback (se actualiza en cada render, sin efectos ni re-renders extra).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const toggleSelect = useCallback((idx, shiftKey) => {
    const currentItems = itemsRef.current;
    setSelectedIds((prev) => {
      let base = new Set(prev);
      if (shiftKey && lastCheckedIdxRef.current != null) {
        const [start, end] = [lastCheckedIdxRef.current, idx].sort((a, b) => a - b);
        for (let i = start; i <= end; i++) {
          const id = currentItems[i]?.__id;
          if (id) base.add(id);
        }
      } else {
        const id = currentItems[idx]?.__id;
        if (!id) return prev;
        if (base.has(id)) base.delete(id); else base.add(id);
      }
      lastCheckedIdxRef.current = idx;
      const { expanded, changed } = expandSelectionWithChildren(currentItems, base);
      setSelectionAutoIncluded(changed);
      return expanded;
    });
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectionAutoIncluded(false);
    lastCheckedIdxRef.current = null;
  }, []);
  // Subir/Bajar: mueve el bloque completo un paso, agrupando ítems no consecutivos.
  const handleMoveSelection = useCallback((direction) => {
    setItems((prev) => {
      const next = moveSelectedBlockStep(prev, selectedIds, direction);
      if (next === prev) return prev;
      if (countOrphanItems(next) > countOrphanItems(prev)) {
        toast.error("No se puede mover ahí: dejaría ítems fuera de toda sección.");
        return prev;
      }
      return next;
    });
  }, [selectedIds]);
  // Eliminación múltiple: solo afecta el borrador en memoria (se descarta si se
  // cierra sin guardar) — mismo comportamiento que removeItem de una sola fila.
  const handleDeleteSelected = useCallback(() => {
    if (!selectedIds.size) return;
    let sections = 0, subsecciones = 0, itemsCount = 0;
    items.forEach((it) => {
      if (!selectedIds.has(it.__id)) return;
      if (it.is_group) { if (Number(it.nivel_jerarquia || 0) === 0) sections++; else subsecciones++; }
      else itemsCount++;
    });
    const parts = [];
    if (sections) parts.push(`${sections} sección${sections > 1 ? "es" : ""}`);
    if (subsecciones) parts.push(`${subsecciones} sub-sección${subsecciones > 1 ? "es" : ""}`);
    if (itemsCount) parts.push(`${itemsCount} ítem${itemsCount > 1 ? "s" : ""}`);
    const partsMsg = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}` : (parts[0] || "");
    const total = sections + subsecciones + itemsCount;
    if (!window.confirm(`¿Eliminar ${total} fila${total > 1 ? "s" : ""} seleccionada${total > 1 ? "s" : ""} (${partsMsg})? Esta acción no se puede deshacer hasta guardar.`)) return;
    setItems((prev) => prev.filter((it) => !selectedIds.has(it.__id)));
    clearSelection();
  }, [selectedIds, items, clearSelection]);

  // CEN — Botón maestro: limpiar materiales (C.U.MAT = 0) para presupuestos solo de M.O.
  const [showZeroMatModal, setShowZeroMatModal] = useState(false);
  const [zeroingMat, setZeroingMat] = useState(false);
  const handleBulkZeroMat = async () => {
    if (!budget?._id) { toast.error("Primero guardá el presupuesto"); return; }
    setZeroingMat(true);
    try {
      const { data } = await axios.post(`${API}/budgets/${budget._id}/bulk-zero-mat`, { confirm: true }, opts);
      hydrateFromBudget(data);
      onBudgetUpdated?.(data);
      setShowZeroMatModal(false);
      toast.success(`Materiales limpiados. Subtotal actualizado a ${fmt(Math.round(data?.subtotal_neto || 0))}`, { duration: 4000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al limpiar materiales");
    } finally {
      setZeroingMat(false);
    }
  };


  // PDF REV3 #7 — Drag & drop: reordenar filas con @dnd-kit
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      // BLOQUE 59 — si la fila arrastrada forma parte de una selección múltiple,
      // mueve el bloque completo junto (conservando orden relativo) en vez de
      // reordenar solo esa fila.
      if (selectedIds.size > 1 && selectedIds.has(active.id)) {
        const next = moveSelectedBlockToTarget(prev, selectedIds, over.id);
        if (next === prev) return prev;
        if (countOrphanItems(next) > countOrphanItems(prev)) {
          toast.error("No se puede soltar ahí: dejaría ítems fuera de toda sección.");
          return prev;
        }
        return next;
      }
      const oldIdx = prev.findIndex((it) => (it.__id || "") === active.id);
      const newIdx = prev.findIndex((it) => (it.__id || "") === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, [selectedIds]);

  // Pedido video 2 #2: importar cómputo desde Excel (básico)
  const computoFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const handleImportComputo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/budgets/import-computo`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      const importedItems = res.data?.items || [];
      if (importedItems.length === 0) {
        toast.error("No se encontraron ítems en el Excel");
        return;
      }
      // Replace items completely with imported ones (REV3 #7: agregar __id estable)
      setItems(ensureIds(importedItems));
      // PDF REV3 #2 + #3 — Marcar cómputo como cargado desde Excel
      setComputoFromExcel(true);
      setComputoSource("excel_basico");
      // Si es presupuesto existente, marcar inmediatamente en el backend
      let autoTransitioned = false;
      if (budget?._id) {
        try {
          const { data: mclData } = await axios.post(`${API}/budgets/${budget._id}/mark-computo-loaded`,
            { source: "excel_basico", items_count: importedItems.filter(i => !i.is_group).length },
            opts);
          // A.3.b — Auto-avance: si el budget estaba en recepcion, ya pasó a cotizacion
          if (mclData?.auto_transitioned) {
            setCurrentStatus("cotizacion");
            if (typeof onBudgetUpdated === "function") onBudgetUpdated(mclData);
            autoTransitioned = true;
          }
        } catch (_err) { /* no bloqueante */ }
      }
      if (autoTransitioned) {
        toast.success(`✓ Cómputo cargado (${res.data.count} ítems). Estado actualizado automáticamente a Cotización.`, { duration: 5000 });
      } else {
        toast.success(`Cómputo cargado: ${res.data.count} ítems (${res.data.groups} secciones) — ya podés pasar a Cotización`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar cómputo");
    } finally {
      setImporting(false);
      if (computoFileRef.current) computoFileRef.current.value = "";
    }
  };

  // V3: importar cómputo con IA (Gemini) — preview antes de confirmar
  const computoIaFileRef = useRef(null);
  const [importingIa, setImportingIa] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const handleImportComputoIa = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImportingIa(true);
    try {
      const res = await axios.post(`${API}/budgets/import-computo-ia`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      setPreviewData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar Excel con IA");
    } finally {
      setImportingIa(false);
      if (computoIaFileRef.current) computoIaFileRef.current.value = "";
    }
  };

  const confirmIaImport = async (importedItems) => {
    setItems(ensureIds(importedItems));
    setPreviewData(null);
    // PDF REV3 #2 + #3 — IA también marca cómputo como cargado
    setComputoFromExcel(true);
    setComputoSource("excel_ia");
    let autoTransitioned = false;
    if (budget?._id) {
      try {
        const { data: mclData } = await axios.post(`${API}/budgets/${budget._id}/mark-computo-loaded`,
          { source: "excel_ia", items_count: importedItems.filter(i => !i.is_group).length },
          opts);
        if (mclData?.auto_transitioned) {
          setCurrentStatus("cotizacion");
          if (typeof onBudgetUpdated === "function") onBudgetUpdated(mclData);
          autoTransitioned = true;
        }
      } catch (_err) { /* no bloqueante */ }
    }
    if (autoTransitioned) {
      toast.success(`✓ Cómputo importado con IA (${importedItems.filter(i => !i.is_group).length} ítems). Estado actualizado automáticamente a Cotización.`, { duration: 5000 });
    } else {
      toast.success(`Cómputo importado con IA: ${importedItems.filter(i => !i.is_group).length} ítems, ${importedItems.filter(i => i.is_group).length} secciones — listo para Cotización`);
    }
  };

  // Fix ET (2026-10-01): "Necesitamos una función nueva tal que se pueda agregar desde un excel
  // una sección e ítems completa que vaya bajo el ya existente dentro del sistema. Y que estos
  // ítems se puedan reordenar e integrarse completamente al presupuesto ya en elaboración, como
  // si fuera que se haya ejecutado de una sola vez. Sin borrar o reemplazar los ítems, secciones
  // y precios ya cargados" (CEN). Lo importado va al final y sus secciones siguen la numeración
  // de las existentes; lo ya cargado no cambia. Se reordena arrastrando, como siempre, y
  // «Renumerar secciones» numera de nuevo todas las secciones según el orden de la planilla.
  const agregarImportados = (importados) => {
    setItems(prev => {
      const dPrev = computeItemNumbers(prev);
      const majors = prev
        .map((it, i) => (it.is_group && Number(it.nivel_jerarquia || 0) === 0 ? parseInt(String(dPrev[i] || "").split(".")[0], 10) : NaN))
        .filter(n => !isNaN(n));
      const offset = majors.length ? Math.max(...majors) : 0;
      const dNew = computeItemNumbers(importados);
      const nuevos = importados.map((it, i) => {
        if (!it.is_group) return it;
        const partes = String(dNew[i] || "").split(".");
        const mayor = parseInt(partes[0], 10);
        partes[0] = String((isNaN(mayor) ? 1 : mayor) + offset);
        return { ...it, numero: partes.join(".") };
      });
      return [...prev, ...ensureIds(nuevos)];
    });
  };

  const agregarFileRef = useRef(null);
  const [agregando, setAgregando] = useState(false);
  const handleAgregarComputo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setAgregando(true);
    try {
      const res = await axios.post(`${API}/budgets/import-computo`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      const importados = res.data?.items || [];
      if (importados.length === 0) {
        toast.error("No se encontraron ítems en el Excel");
        return;
      }
      agregarImportados(importados);
      toast.success(`Agregado al final: ${importados.filter(i => !i.is_group).length} ítems y ${importados.filter(i => i.is_group).length} secciones. Lo ya cargado no cambió — guardá para conservarlo.`, { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo agregar el Excel");
    } finally {
      setAgregando(false);
      if (agregarFileRef.current) agregarFileRef.current.value = "";
    }
  };

  const confirmIaAgregar = (importados) => {
    agregarImportados(importados);
    setPreviewData(null);
    toast.success(`Agregado al final: ${importados.filter(i => !i.is_group).length} ítems y ${importados.filter(i => i.is_group).length} secciones. Lo ya cargado no cambió — guardá para conservarlo.`, { duration: 6000 });
  };

  const renumerarSecciones = useCallback(() => {
    if (!window.confirm("Se numeran de nuevo todas las secciones según el orden actual de la planilla (1.0, 2.0… y sus sub-secciones). Los ítems siguen a su sección. ¿Seguir?")) return;
    setItems(prev => {
      const sinNumero = prev.map(it => {
        if (!it.is_group) return it;
        const partes = String(it.numero || "").split(".").map(s => s.trim()).filter(Boolean);
        while (partes.length > 1 && partes[partes.length - 1] === "0") partes.pop();
        return { ...it, numero: "", nivel_jerarquia: partes.length ? partes.length - 1 : Number(it.nivel_jerarquia || 0) };
      });
      const disp = computeItemNumbers(sinNumero);
      return sinNumero.map((it, i) => (it.is_group ? { ...it, numero: disp[i] } : it));
    });
  }, []);

  // Pedido video 2 #3: calcular groupPct por índice (último grupo antes del item)
  const groupPctByIdx = useMemo(() => {
    const map = [];
    let current = null;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.is_group) {
        // FIX jerarquía — solo los grupos nivel 0 definen el contexto de %.
        // Los sub-grupos (nivel>=1) lo heredan del grupo principal.
        if (Number(it.nivel_jerarquia || 0) === 0) {
          const gp = {
            ganancia_mat_pct: it.ganancia_mat_pct,
            ga_pct: it.ga_pct,
            gmo_pct: it.gmo_pct,
          };
          const hasAny = Object.values(gp).some(v => v != null && v !== "");
          current = hasAny ? gp : null;
        }
        map.push(null);
      } else {
        map.push(current);
      }
    }
    return map;
  }, [items]);

  // PDF REV1 E.1: Σ mat_sin_iva por grupo (excluyendo ítems "menores_pct") para auto-cálculo.
  const groupMatTotalByIdx = useMemo(() => {
    // 1) Calcular sumas por groupIdx
    const sums = new Map();
    let gIdx = -1;
    const idxToGroup = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.is_group) {
        // FIX jerarquía v2 — cada grupo Y sub-grupo inicia un nuevo grupo de cálculo
        // para los "menores_pct" (% sobre los materiales de su sub-sección).
        gIdx = i; sums.set(gIdx, 0);
        idxToGroup.push(-1);
        continue;
      }
      idxToGroup.push(gIdx);
      if (it.tipo_calculo === "menores_pct") continue;
      const cant = parseFloat(it.cantidad || 0);
      let cu = parseFloat(it.costo_unit_mat || 0);
      if (precioInputMode === "con_iva") cu = cu / 1.10;
      if (gIdx >= 0) sums.set(gIdx, (sums.get(gIdx) || 0) + cant * cu);
    }
    return idxToGroup.map(g => g >= 0 ? (sums.get(g) || 0) : 0);
  }, [items, precioInputMode]);

  const tcHoy = useTipoCambio(null, form.moneda === "USD");   // Fix FG
  const tcCotiz = Number(form.tipo_cambio) || tcHoy?.usd_pyg || 0;
  const totals = useMemo(() => {
    const r = computeBudgetLive(items, pct, precioInputMode, descuento);
    return {
      subtotalNeto: r.subtotal_neto,
      iva: r.iva,
      total: r.total,
      descuento_activo: r.descuento_activo,
      descuento_monto: r.descuento_monto,
      descuento_base: r.descuento_base,
      descuento_porcentaje: r.descuento_porcentaje,
      subtotal_con_descuento: r.subtotal_con_descuento,
    };
  }, [items, pct, precioInputMode, descuento]);

  // Fix HW (2026-10-27): "lo que se busca con el descuento a la mano de obra es un descuento final del 5% sobre el
  // ppto total" (CEN). Se escribe el descuento final sobre el total y el sistema calcula el % sobre la mano de obra.
  const descFinalPct = totals.subtotalNeto > 0 ? Number(((totals.descuento_monto / totals.subtotalNeto) * 100).toFixed(3)) : 0;
  const descFinalMax = totals.subtotalNeto > 0 ? Number(((totals.descuento_base / totals.subtotalNeto) * 100).toFixed(3)) : 0;
  const cambiarDescFinal = (txt) => {
    setDescFinalTxt(txt);
    const meta = parseFloat(txt);
    if (!(meta > 0) || !(totals.descuento_base > 0) || !(totals.subtotalNeto > 0)) {
      setDescuento(d => ({ ...d, porcentaje: 0 }));
      return;
    }
    const pctMo = ((meta / 100) * totals.subtotalNeto / totals.descuento_base) * 100;
    setDescuento(d => ({ ...d, porcentaje: Math.min(100, Number(pctMo.toFixed(6))) }));
  };

  // CEN Jun 2026 — N° jerárquico auto-correlativo (corrige "1.1" en Materiales menores e ítems nuevos).
  const itemNumbers = useMemo(() => computeItemNumbers(items), [items]);

  // PDF REV3 — payload reutilizable (handleSave / handleSaveDraft / handleCreateRevision)
  // FIX jerarquía (Jun 2026) — para filas de grupo con N°, el nivel se deriva del N°
  // (segmentos - 1), consistente con el backend. Sin N° conserva el nivel del botón.
  const nivelFromNumero = (numero) => {
    const parts = String(numero || "").split(".").map(s => s.trim()).filter(Boolean);
    while (parts.length > 1 && parts[parts.length - 1] === "0") parts.pop();
    return Math.max(0, parts.length - 1);
  };
  const buildItemsPayload = () => {
    // CEN Jun 2026 — persistir el N° efectivo auto-correlativo para ítems (incl. Materiales
    // menores con numero=""); los grupos conservan su N° manual (o vacío) como hasta ahora.
    const nums = computeItemNumbers(items);
    return items.map((i, idx) => ({
    // CEN 2.0 FASE 1 (CIMIENTOS) — identidad estable: si el ítem ya tiene item_uid
    // (vino del backend), se reenvía tal cual. Si es nuevo, se omite y el backend
    // genera uno en el primer guardado (calc_item). NUNCA se inventa uno en el cliente.
    item_uid: i.item_uid || undefined,
    numero: i.is_group ? (i.numero || "") : (nums[idx] || ""), descripcion: i.descripcion || "", unidad: i.unidad || "un",
    cantidad: parseFloat(i.cantidad) || 0,
    costo_unit_mat: parseFloat(i.costo_unit_mat) || 0,
    costo_unit_mo: parseFloat(i.costo_unit_mo) || 0,
    is_group: !!i.is_group, observaciones: i.observaciones || "",
    nivel_jerarquia: (i.is_group && String(i.numero || "").trim())
      ? nivelFromNumero(i.numero)
      : (Number(i.nivel_jerarquia) || 0),
    combinator_mecanismos: Array.isArray(i.combinator_mecanismos) ? i.combinator_mecanismos : null,
    combinator_cables: Array.isArray(i.combinator_cables) ? i.combinator_cables : null,
    ganancia_mat_pct: i.ganancia_mat_pct != null && i.ganancia_mat_pct !== "" ? parseFloat(i.ganancia_mat_pct) : null,
    ga_pct: i.ga_pct != null && i.ga_pct !== "" ? parseFloat(i.ga_pct) : null,
    gmo_pct: i.gmo_pct != null && i.gmo_pct !== "" ? parseFloat(i.gmo_pct) : null,
    tipo_calculo: i.tipo_calculo || "fijo",
    menores_pct: i.menores_pct != null && i.menores_pct !== "" ? parseFloat(i.menores_pct) : null,
    // Campos de las fases 2-5 (sin UI todavía en esta fase) — se preservan si ya existían.
    subitems: Array.isArray(i.subitems) ? i.subitems : null,
    tareas: Array.isArray(i.tareas) ? i.tareas : null,
    nivel_id: i.nivel_id || null,
    sector_label: i.sector_label || null,
    catalogo_ref: i.catalogo_ref || null,
    // Fix AJ (2026-09-04): metraje de la boca medido en esta obra.
    boca_metraje: i.boca_metraje || null,
    match_score: i.match_score != null ? i.match_score : null,
    match_status: i.match_status || null,
    }));
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre del presupuesto es obligatorio"); return; }
    // A.1 — Cerrar modal de confirmación si estaba abierto (llamado desde modal "Sí, crear Rev N+1")
    setShowUpdateConfirm(false);
    setSaving(true);
    try {
      const payload = {
        ...form,
        porcentajes: pct,
        precio_input_mode: precioInputMode,
        descuento: descuento,
        items: buildItemsPayload(),
      };
      if (budget?._id) {
        const { data } = await axios.put(`${API}/budgets/${budget._id}`, payload, opts);
        // Bug fix 19/05/2026: re-hidratar local + propagar al padre antes
        // de cerrar, así la lista deja de mostrar stale incluso si onSave
        // tarda en ejecutar fetchAll.
        if (data) {
          hydrateFromBudget(data);
          if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
        }
        if (data?.__auto_snapshot_created) {
          toast.success(
            "Presupuesto actualizado. Se creó un snapshot automático de la versión anterior (cambio significativo detectado).",
            { duration: 5000 }
          );
        } else {
          toast.success(`Presupuesto actualizado — Revisión ${data?.revision ?? ""}`);
        }
      } else {
        // PDF REV3 #2 + #3 — al crear, indicar si el cómputo vino de Excel
        payload.computo_disponible = !!computoFromExcel;
        payload.computo_source = computoSource || "";
        await axios.post(`${API}/budgets`, payload, opts);
        toast.success(parentBudget ? "Presupuesto adicional creado" : "Presupuesto creado");
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  // A.1 — Wrapper del botón "Actualizar Presupuesto": para budgets existentes,
  // muestra confirmación antes de incrementar la revisión. Para budgets nuevos,
  // llama directamente a handleSave (POST create).
  const handleSaveClick = () => {
    if (!form.nombre.trim()) { toast.error("El nombre del presupuesto es obligatorio"); return; }
    if (budget?._id) {
      setShowUpdateConfirm(true); // → modal de confirmación
    } else {
      handleSave(); // nuevo presupuesto → POST create directo
    }
  };

  // PDF REV3 #4 — Guardar Avance: persiste cambios SIN incrementar revisión
  const handleSaveDraft = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre del presupuesto es obligatorio"); return; }
    if (!budget?._id) {
      // Para presupuesto nuevo, "Guardar Avance" hace un create normal
      return handleSave();
    }
    setSavingDraft(true);
    try {
      const payload = {
        ...form,
        porcentajes: pct,
        precio_input_mode: precioInputMode,
        descuento: descuento,
        items: buildItemsPayload(),
      };
      const { data } = await axios.post(`${API}/budgets/${budget._id}/save-draft`, payload, opts);
      // Bug fix 19/05/2026: re-hidratar state local con la respuesta del
      // backend (incluye items normalizados con totales calculados) Y
      // propagar al padre para que la lista deje de mostrar stale.
      if (data) {
        hydrateFromBudget(data);
        if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
      } else {
        setLastDraftAt(new Date().toISOString());
      }
      toast.success("Avance guardado — sin crear revisión nueva");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar avance");
    } finally { setSavingDraft(false); }
  };

  // A.3.c — Transición de estado dentro del form (cotizacion→revision, revision→enviado, enviado→aprobado/rechazado)
  const handleTransitionInForm = async () => {
    const targetStatus = showTransitionConfirm;
    if (!targetStatus || !budget?._id) return;
    setTransitionLoading(true);
    try {
      let data;
      if (targetStatus === "rechazado") {
        ({ data } = await axios.post(`${API}/budgets/${budget._id}/reject`,
          { comment: transitionComment || "Rechazado" }, opts));
      } else {
        ({ data } = await axios.post(`${API}/budgets/${budget._id}/transition`,
          { comment: transitionComment || "" }, opts));
      }
      setCurrentStatus(data.status || targetStatus);
      if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
      const statusLabel = { revision: "Revisión", enviado: "Enviado", aprobado: "Aprobado", rechazado: "Rechazado" }[data.status] || data.status;
      toast.success(`Estado actualizado: ${statusLabel}`);
      setShowTransitionConfirm(null);
      setTransitionComment("");
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar estado");
    } finally { setTransitionLoading(false); }
  };

  // FIX 1 — Cambio manual de estado (sin seguir el flujo, sin tocar la revisión)
  const handleManualStatusConfirm = async () => {
    if (!manualStatusTarget || !budget?._id) return;
    setManualStatusLoading(true);
    try {
      const { data } = await axios.put(
        `${API}/budgets/${budget._id}/status-manual`,
        { status: manualStatusTarget, reason: manualStatusReason || "" },
        opts
      );
      setCurrentStatus(data.status || manualStatusTarget);
      if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
      toast.success(`Estado cambiado a: ${manualStatusLabel(data.status || manualStatusTarget)}`);
      setManualStatusTarget(null);
      setManualStatusReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar el estado");
    } finally { setManualStatusLoading(false); }
  };



  // PDF REV3 #5 + #6 — Crear Revisión: snapshotea la versión actual + persiste cambios
  const handleCreateRevision = async (comment = "") => {
    if (!form.nombre.trim()) { toast.error("El nombre del presupuesto es obligatorio"); return; }
    if (!budget?._id) {
      toast.error("Primero hay que guardar el presupuesto antes de crear una revisión");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        comment: comment || "Nueva revisión",
        payload: {
          ...form,
          porcentajes: pct,
          precio_input_mode: precioInputMode,
          descuento: descuento,
          items: buildItemsPayload(),
        },
      };
      const { data } = await axios.post(`${API}/budgets/${budget._id}/create-revision`, payload, opts);
      // Bug fix 19/05/2026: re-hidratar + propagar al padre antes de cerrar.
      if (data) {
        hydrateFromBudget(data);
        if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
      }
      toast.success("Nueva revisión creada — la versión anterior quedó archivada");
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear revisión");
    } finally { setSaving(false); }
  };

  // PDF REV3 #4 — Helper de formato "hace X min/h" para indicador de borrador
  const draftIndicator = useMemo(() => {
    if (!lastDraftAt) return null;
    try {
      const elapsedMs = Date.now() - new Date(lastDraftAt).getTime();
      const m = Math.floor(elapsedMs / 60000);
      if (m < 1) return "Borrador guardado hace unos segundos";
      if (m < 60) return `Borrador guardado hace ${m} min`;
      const h = Math.floor(m / 60);
      if (h < 24) return `Borrador guardado hace ${h} h`;
      const d = Math.floor(h / 24);
      return `Borrador guardado hace ${d} d`;
    } catch { return null; }
  }, [lastDraftAt]);

  // BLOQUE 54/61 — Virtualización de la tabla de ítems (Estructura de Cómputo).
  // Con cómputos de ~2.000 filas, montar TODOS los <tr> a la vez (cada uno con
  // textarea auto-grow + dnd-kit sortable) congelaba el navegador al importar.
  // Ahora solo se montan las filas visibles + margen (overscan); el resto se
  // representa con 2 filas "spacer" que preservan el alto total del scroll.
  // NO altera el modelo de datos ni el orden — es puramente de renderizado.
  //
  // BLOQUE 61 — se sacó `measureElement` (medición real del DOM vía
  // ResizeObserver por fila). El textarea de descripción tiene auto-grow (ver
  // ItemRow) y CADA fila recién montada mutaba su propio alto → disparaba el
  // ResizeObserver interno de @tanstack/react-virtual → éste recalculaba los
  // offsets acumulados de TODAS las filas restantes → forzaba un re-render de
  // este componente → dnd-kit (SortableContext) recomputaba posiciones de las
  // filas montadas → nuevas lecturas de layout que podían volver a disparar el
  // observer. Con ~50 filas montadas de una sola vez (overscan) este ciclo se
  // retroalimentaba y terminaba bloqueando el hilo del navegador al ABRIR un
  // cómputo grande. `estimateSize` ahora es una función PURA (sin leer el DOM)
  // basada en el largo de la descripción — se calcula una sola vez por fila y
  // nunca se remide, eliminando la retroalimentación por completo.
  const estimateRowSize = useCallback((index) => {
    const it = items[index];
    if (!it) return 32;
    if (it.is_group) return Number(it.nivel_jerarquia || 0) >= 1 ? 30 : 36;
    const len = (it.descripcion || "").length;
    const lines = Math.max(1, Math.ceil(len / 55));
    return 30 + (lines - 1) * 15;
  }, [items]);
  const itemsScrollRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => itemsScrollRef.current,
    estimateSize: estimateRowSize,
    overscan: 12,
  });
  // BLOQUE 62 — BUG introducido por el fix de Bloque 61: al sacar `measureElement`
  // (por fila) también se perdió, sin querer, el único mecanismo que forzaba al
  // virtualizador a volver a medir el CONTENEDOR de scroll. Si en el primer
  // cálculo el contenedor todavía tiene alto 0 (el modal recién está montando/
  // animando, el layout flex aún no se asentó), `getVirtualItems()` devuelve un
  // rango vacío y —a diferencia de antes— nada le avisa que ya hay espacio real:
  // la tabla queda sin ninguna fila (0 filas, clientHeight:0) aunque `items` en
  // el estado siga teniendo los 1.830 ítems intactos. Con pocos ítems el primer
  // render es más liviano y suele terminar DESPUÉS de que el layout se asienta,
  // por eso el bug era invisible en presupuestos chicos.
  // Fix: UN SOLO ResizeObserver sobre el CONTENEDOR (no uno por fila — eso fue
  // lo que causó el cuelgue de Bloque 61) que le pide al virtualizador remedir
  // en cuanto el contenedor obtiene tamaño real, más una medición explícita en
  // el próximo frame por si ya lo tenía pero se midió antes del primer paint.
  useEffect(() => {
    const el = itemsScrollRef.current;
    if (!el) return;
    const remeasure = () => rowVirtualizer.measure();
    const ro = new ResizeObserver(remeasure);
    ro.observe(el);
    const raf = requestAnimationFrame(remeasure);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
    // BLOQUE 62 — el contenedor se desmonta/remonta cada vez que `tab` deja de
    // ser "items" y vuelve (está bajo un `{tab === "items" && (...)}`), así que
    // el efecto debe re-adjuntar el observer cada vez que se vuelve a esa pestaña.
  }, [tab, rowVirtualizer]);
  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const virtualPaddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const virtualPaddingBottom = virtualRows.length > 0 ? virtualTotalSize - virtualRows[virtualRows.length - 1].end : 0;
  const itemsTableColCount = 6 + (isProyectista ? 0 : 15) + 1; // drag+select+num+desc+un+cant + [precios] + remove
  // BLOQUE 61 — id array memoizado para dnd-kit: antes se recreaba con `.map()`
  // en cada render (nueva identidad de array siempre), lo que hacía que
  // SortableContext propagara un contexto "cambiado" a cada fila montada en
  // CADA re-render (incluso los disparados por el propio scroll/virtualizer),
  // forzando a dnd-kit a recalcular innecesariamente. Ahora solo cambia cuando
  // `items` realmente cambia (edición/orden/alta/baja real).
  const sortableItemIds = useMemo(() => items.map((it, idx) => it.__id || `row-${idx}`), [items]);
  // BLOQUE 59 — límites del bloque seleccionado, para deshabilitar Subir/Bajar en los extremos.
  const selectionBounds = useMemo(() => {
    if (!selectedIds.size) return { atTop: true, atBottom: true };
    const idxs = [];
    items.forEach((it, i) => { if (selectedIds.has(it.__id)) idxs.push(i); });
    if (!idxs.length) return { atTop: true, atBottom: true };
    return { atTop: idxs[0] <= 0, atBottom: idxs[idxs.length - 1] >= items.length - 1 };
  }, [items, selectedIds]);

  return (
    // Fix CC (2026-09-10): pestañas Bocas / M.O. / Catálogos, buscador de precios de
    // cada ítem y autollenado usan la copia de ESTE presupuesto (catalogoScope.jsx).
    <CatalogoPresupuestoContext.Provider value={budget?._id || PRESUPUESTO_SIN_GUARDAR}>
    <div className="flex flex-col h-full relative">
      <div className="bg-[#2E6DA4] text-white px-6 py-4 flex-shrink-0">
        <h3 className="font-bold text-base tracking-wide">SISTEMA DE GESTIÓN DE PRESUPUESTOS — CEN INGENIERIA E.A.S.</h3>
        <p className="text-xs opacity-70 mt-0.5">Instalaciones Eléctricas — Paraguay</p>
      </div>

      {/* Bug fix 19/05/2026 — Overlay mientras hidratamos desde el backend para
          evitar que el usuario edite/guarde basándose en datos cacheados.
          BLOQUE 61 — con presupuestos grandes (>500 ítems) el mensaje avisa
          explícitamente que puede demorar unos segundos, en vez de dejar la
          pantalla en blanco/congelada sin ningún indicio de progreso. */}
      {hydrating && (
        <div data-testid="budget-hydrating-overlay" className="absolute inset-0 z-50 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
          <Loader2 className="animate-spin text-[#2E6DA4]" size={32} />
          <p className="text-sm font-semibold text-[#1A4B7A]">
            {items.length > 500 ? `Cargando cómputo grande (${items.length.toLocaleString("es-PY")} ítems)…` : "Cargando última versión desde el servidor…"}
          </p>
          <p className="text-xs text-gray-500">{items.length > 500 ? "Puede tardar unos segundos — no cierres esta ventana." : "Evitando trabajar sobre datos cacheados."}</p>
        </div>
      )}


      {/* BARRA COMPACTA SIEMPRE VISIBLE */}
      <div className="px-6 py-2 bg-[#EBF3FB] border-b border-[#C5DCF0] flex-shrink-0 flex items-center gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[9px] text-gray-500 uppercase tracking-widest">Presupuesto</p>
          <p className="text-sm font-bold text-[#1A4B7A] truncate max-w-[220px]">{form.nombre || <span className="font-normal italic text-gray-400">Sin nombre</span>}</p>
        </div>
        {form.cliente && (
          <div className="border-l border-[#C5DCF0] pl-4 min-w-0">
            <p className="text-[9px] text-gray-500 uppercase tracking-widest">Cliente</p>
            <p className="text-sm font-semibold text-gray-700 truncate max-w-[180px]">{form.cliente}</p>
          </div>
        )}
        {!isProyectista && (
          <div className="border-l border-[#C5DCF0] pl-4 flex-shrink-0">
            <p className="text-[9px] text-gray-500 uppercase tracking-widest">Márgenes</p>
            <p className="text-[11px] font-bold text-[#1A4B7A]">GM {pct.ganancia_mat_pct}% · GA {pct.ga_pct}% · GMO {pct.gmo_pct}%</p>
          </div>
        )}
        {!isProyectista && (
          <div className="border-l border-[#C5DCF0] pl-4 flex-shrink-0">
            <p className="text-[9px] text-gray-500 uppercase tracking-widest">Modo precios</p>
            <p className={`text-[11px] font-bold ${precioInputMode === "con_iva" ? "text-emerald-700" : "text-gray-600"}`} data-testid="precio-mode-badge">
              {precioInputMode === "con_iva" ? "CON IVA" : "SIN IVA"}
            </p>
          </div>
        )}
        <div className="ml-auto flex items-center gap-4 flex-shrink-0">
          {/* FIX 1 — Cambiar estado manualmente (solo roles permitidos, presupuesto guardado) */}
          {canChangeStatusManual && budget?._id && (
            <div className="flex items-center gap-1.5" data-testid="manual-status-wrap">
              <span className="text-[9px] text-gray-500 uppercase tracking-widest">Estado</span>
              <select
                value={MANUAL_STATUS_OPTIONS.find(s => s.key === currentStatus) ? currentStatus : "recepcion"}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && v !== currentStatus) {
                    if (v === "aprobado" && isAdicional) { setShowAdicionalModal(true); }
                    else { setManualStatusTarget(v); setManualStatusReason(""); }
                  }
                }}
                className="text-[11px] font-bold text-[#1A4B7A] border border-[#C5DCF0] rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-[#2E6DA4]/30 cursor-pointer"
                data-testid="manual-status-select"
                title="Cambiar el estado del presupuesto manualmente"
              >
                {MANUAL_STATUS_OPTIONS.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          )}
          {tab !== "datos" && (
            <button onClick={() => setTab("datos")}
              className="text-[11px] text-[#2E6DA4] hover:text-[#1A4B7A] font-semibold hover:underline underline-offset-2 transition-colors">
              Editar datos →
            </button>
          )}
          {!isProyectista && (
            <div className="text-right">
              <p className="text-[9px] text-gray-400 uppercase tracking-widest">Total IVA inc.</p>
              <p className="text-base font-bold text-[#1A4B7A] tabular-nums">{fmt(Math.round(totals.total))}</p>
              {form.moneda === "USD" && tcCotiz > 0 && <p className="text-[11px] font-semibold text-blue-700 tabular-nums" data-testid="budget-header-usd">{formatUSD2(totals.total / tcCotiz)}</p>}
            </div>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-100 bg-white flex-shrink-0 px-6 overflow-x-auto">
        {(isProyectista
          ? [["datos", "Datos del Presupuesto"], ["items", "Estructura de Cómputo"], ["bocas", "Bocas Eléctricas"], ["mo", "Mano de Obra"], ["alcance", "Alcance / Observaciones"]]
          : [["datos", "Datos del Presupuesto"], ["items", "Estructura de Cómputo"], ["bocas", "Bocas Eléctricas"], ["mo", "Mano de Obra"], ["catalogos", "Catálogos"], ["financials", "Análisis Financiero"], ["alcance", "Alcance / Observaciones"]]
        ).map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${tab === t ? "border-[#2E6DA4] text-[#2E6DA4]" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            data-testid={`tab-${t}-btn`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "datos" && (
        <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-gray-50">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-[#1A4B7A] mb-5 uppercase tracking-wide flex items-center gap-2">
              <FileText size={14} /> Datos Generales del Presupuesto
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nombre del Presupuesto *</label>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
                  placeholder="Ej: Instalación Eléctrica Edificio San Salvador" data-testid="budget-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo</label>
                <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30">
                  <option value="obra">Obra</option>
                  <option value="proyecto">Proyecto</option>
                  <option value="varios">Varios</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo de instalación</label>
                <select value={form.tipo_instalacion || ""} onChange={e => setForm(f => ({ ...f, tipo_instalacion: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30"
                  data-testid="budget-tipo-instalacion-select">
                  <option value="">— Sin clasificar —</option>
                  {tiposInstalacion.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-600">A: (Cliente)</label>
                  <button type="button" onClick={() => setShowNewCliente(true)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:text-[#1f4f78]"
                    data-testid="budget-new-cliente-btn">
                    <Plus size={12} /> Nuevo cliente
                  </button>
                </div>
                <select value={form.cliente_id} onChange={e => {
                  const cl = (clientesAll || []).find(c => c._id === e.target.value);
                  setForm(f => ({ ...f, cliente_id: e.target.value, cliente: cl?.cliente || f.cliente, cliente_codigo: cl?.codigo || "" }));
                }} className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30" data-testid="budget-cliente-select">
                  <option value="">-- Sin asignar / Manual --</option>
                  {(clientesAll || []).map(c => <option key={c._id} value={c._id}>{`${c.codigo} — ${c.cliente}`}</option>)}
                </select>
                {!form.cliente_id && (
                  <input value={form.cliente} onChange={e => setForm(f => ({ ...f, cliente: e.target.value }))}
                    className="w-full mt-1.5 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                    placeholder="Nombre del cliente (manual)" data-testid="budget-cliente-manual-input" />
                )}
                {showNewCliente && (
                  <ClienteFormModal
                    initial={null}
                    onClose={() => setShowNewCliente(false)}
                    onSaved={handleClienteCreated}
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">ATN: (Ingeniero de Contacto)</label>
                <input value={form.ingeniero_contacto} onChange={e => setForm(f => ({ ...f, ingeniero_contacto: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                  placeholder="Ing. / Arq. de contacto" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-gray-600">Obra</label>
                  <button type="button" onClick={() => setShowNewObra(true)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7C3AED] hover:text-[#5b21b6]"
                    data-testid="budget-new-obra-btn">
                    <Plus size={12} /> Nueva Obra
                  </button>
                </div>
                <select value={form.obra_id} onChange={e => {
                  const ob = obrasAll.find(o => o._id === e.target.value);
                  setForm(f => ({ ...f, obra_id: e.target.value, obra: ob?.obra || (e.target.value ? f.obra : f.obra) }));
                }} className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30" data-testid="budget-obra-select">
                  <option value="">-- Sin asignar / Manual --</option>
                  {obrasDelCliente.map(o => <option key={o._id} value={o._id}>{`${o.codigo} — ${o.obra}`}</option>)}
                </select>
                {!form.obra_id && (
                  <input value={form.obra} onChange={e => setForm(f => ({ ...f, obra: e.target.value }))}
                    className="w-full mt-1.5 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                    placeholder="Nombre de la obra (manual)" data-testid="budget-obra-manual-input" />
                )}
                {showNewObra && (
                  <ObraFormModal
                    initial={null}
                    clientes={clientesAll}
                    preselectClienteId={form.cliente_id}
                    onClose={() => setShowNewObra(false)}
                    onSaved={handleObraCreated}
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha de elaboración</label>
                <input type="date" value={form.fecha_elaboracion} onChange={e => setForm(f => ({ ...f, fecha_elaboracion: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                  data-testid="budget-fecha-elaboracion-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Deadline</label>
                <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                  data-testid="budget-deadline-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Iniciales Elaborador</label>
                <input value={form.elaborator_initials}
                  onChange={e => setForm(f => ({ ...f, elaborator_initials: e.target.value.toUpperCase().slice(0, 4) }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-[#2E6DA4]/30"
                  placeholder="EV" maxLength={4} data-testid="budget-initials-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Proyecto ERP</label>
                <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/30">
                  <option value="">Sin proyecto</option>
                  {projects.map(p => <option key={p._id} value={p._id}>{`${p.code} · ${p.client || "—"} · ${p.name}`}</option>)}
                </select>
              </div>
            </div>
          </div>

          {!isProyectista && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide flex items-center gap-2">
              <Percent size={14} /> Márgenes Globales
            </h3>
            <p className="text-xs text-gray-500 mb-5">Aplicados a TODOS los ítems. Las secciones pueden sobreescribir con sus propios %.</p>
            <div className="flex flex-wrap items-start gap-8">
              {[
                { key: "ganancia_mat_pct", label: "G.Mat", sublabel: "Ganancia Materiales" },
                { key: "ga_pct", label: "G.A.", sublabel: "Gastos Administrativos" },
                { key: "gmo_pct", label: "G.M.O.", sublabel: "Ganancia Mano de Obra" }
              ].map(({ key, label, sublabel }) => (
                <div key={key} className="flex flex-col items-center gap-1.5">
                  <span className="text-xs font-bold text-gray-700">{label}</span>
                  <span className="text-[10px] text-gray-400">{sublabel}</span>
                  <div className="flex items-center border-2 border-[#2E6DA4] rounded-xl overflow-hidden bg-white shadow-sm">
                    <input type="number" min="0" max="100" value={pct[key]}
                      onChange={e => setPct(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                      className="w-16 text-lg font-bold text-[#1A4B7A] text-center py-2 focus:outline-none"
                      data-testid={`pct-${key}`} />
                    <span className="text-base text-[#1A4B7A] font-bold px-2">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Pedido video 2 #4 — Toggle modo de captura de precios */}
          {!isProyectista && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide flex items-center gap-2">
              <Wallet size={14} /> Modo de captura de precios (IVA)
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Define cómo ingresás los precios de Materiales y Mano de Obra en la planilla. Modelo Excel CEN: <strong>Con IVA</strong>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${precioInputMode === "con_iva" ? "border-[#2E6DA4] bg-[#EBF3FB]" : "border-gray-200 hover:border-gray-300"}`}>
                <input
                  type="radio"
                  name="precio-input-mode"
                  value="con_iva"
                  checked={precioInputMode === "con_iva"}
                  onChange={() => setPrecioInputMode("con_iva")}
                  className="mt-1 accent-[#2E6DA4]"
                  data-testid="precio-mode-con-iva"
                />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1A4B7A]">Con IVA incluido <span className="text-[10px] font-normal text-emerald-600">(Recomendado · Modelo Excel CEN)</span></p>
                  <p className="text-[11px] text-gray-600 mt-1">Ingresás el precio bruto del proveedor. El sistema calcula automáticamente:</p>
                  <p className="text-[11px] text-gray-700 mt-1 font-mono">Mat S/IVA = Precio − Precio/11</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${precioInputMode === "sin_iva" ? "border-[#2E6DA4] bg-[#EBF3FB]" : "border-gray-200 hover:border-gray-300"}`}>
                <input
                  type="radio"
                  name="precio-input-mode"
                  value="sin_iva"
                  checked={precioInputMode === "sin_iva"}
                  onChange={() => setPrecioInputMode("sin_iva")}
                  className="mt-1 accent-[#2E6DA4]"
                  data-testid="precio-mode-sin-iva"
                />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1A4B7A]">Sin IVA <span className="text-[10px] font-normal text-gray-500">(Legacy)</span></p>
                  <p className="text-[11px] text-gray-600 mt-1">Ingresás el precio neto. El sistema agrega 10% para obtener el bruto:</p>
                  <p className="text-[11px] text-gray-700 mt-1 font-mono">Total c/IVA = Mat × 1.10</p>
                </div>
              </label>
            </div>
          </div>
          )}

          {/* Descuento (Feb 2026) */}
          {!isProyectista && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="descuento-section">
            <h3 className="text-sm font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide flex items-center gap-2">
              <Percent size={14} /> Descuento
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Aplicar un descuento sobre un componente del presupuesto. Se resta ANTES del IVA.
            </p>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={descuento.activo}
                onChange={(e) => setDescuento(d => ({ ...d, activo: e.target.checked }))}
                className="w-4 h-4 accent-[#2E6DA4] rounded"
                data-testid="descuento-activo-check"
              />
              <span className="text-sm font-semibold text-gray-700">Aplicar descuento</span>
            </label>
            {descuento.activo && (
              <div className="space-y-4 pl-7 border-l-2 border-[#2E6DA4]/20">
                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Porcentaje</label>
                    <div className="flex items-center border-2 border-[#2E6DA4] rounded-xl overflow-hidden bg-white shadow-sm">
                      <input
                        type="number" min="0" max="100" step="0.5"
                        value={descuento.porcentaje}
                        onChange={(e) => { setDescFinalTxt(null); setDescuento(d => ({ ...d, porcentaje: parseFloat(e.target.value) || 0 })); }}
                        className="w-28 text-lg font-bold text-[#1A4B7A] text-center py-2 focus:outline-none"
                        data-testid="descuento-porcentaje-input"
                      />
                      <span className="text-base text-[#1A4B7A] font-bold px-2">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Aplica sobre</label>
                    <div className="flex gap-3">
                      <label className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl cursor-pointer transition-all text-sm font-semibold ${descuento.aplica_sobre === "utilidades" ? "border-[#2E6DA4] bg-[#EBF3FB] text-[#1A4B7A]" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                        <input type="radio" name="desc-aplica" value="utilidades"
                          checked={descuento.aplica_sobre === "utilidades"}
                          onChange={() => setDescuento(d => ({ ...d, aplica_sobre: "utilidades" }))}
                          className="accent-[#2E6DA4]"
                          data-testid="descuento-aplica-utilidades"
                        />
                        Utilidades
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-xl cursor-pointer transition-all text-sm font-semibold ${descuento.aplica_sobre === "mano_de_obra" ? "border-[#2E6DA4] bg-[#EBF3FB] text-[#1A4B7A]" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                        <input type="radio" name="desc-aplica" value="mano_de_obra"
                          checked={descuento.aplica_sobre === "mano_de_obra"}
                          onChange={() => setDescuento(d => ({ ...d, aplica_sobre: "mano_de_obra" }))}
                          className="accent-[#2E6DA4]"
                          data-testid="descuento-aplica-mo"
                        />
                        Mano de Obra
                      </label>
                    </div>
                  </div>
                  {descuento.aplica_sobre === "mano_de_obra" && (
                    <div data-testid="descuento-final-bloque">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Descuento final sobre el total</label>
                      <div className="flex items-center border-2 border-[#2E6DA4] rounded-xl overflow-hidden bg-white shadow-sm">
                        <input
                          type="number" min="0" max="100" step="any"
                          value={descFinalTxt ?? String(descFinalPct)}
                          onChange={(e) => cambiarDescFinal(e.target.value)}
                          onBlur={() => setDescFinalTxt(null)}
                          className="w-24 text-lg font-bold text-[#1A4B7A] text-center py-2 focus:outline-none"
                          data-testid="descuento-final-input"
                        />
                        <span className="text-base text-[#1A4B7A] font-bold px-2">%</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Live preview */}
                <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1" data-testid="descuento-preview">
                  <div className="flex justify-between text-gray-600">
                    <span>Base afectada ({descuento.aplica_sobre === "mano_de_obra" ? "M.O. Directo" : "G.Mat + G.A. + G.MO"}):</span>
                    <span className="font-semibold tabular-nums">{fmt(Math.round(totals.descuento_base || 0))}</span>
                  </div>
                  <div className="flex justify-between text-red-600 font-bold">
                    <span>Monto descontado (-{Number(Number(descuento.porcentaje || 0).toFixed(3))}%):</span>
                    <span className="tabular-nums">-{fmt(Math.round(totals.descuento_monto || 0))}</span>
                  </div>
                  {descuento.aplica_sobre === "mano_de_obra" && (
                    <p className="text-xs text-gray-500 pt-1" data-testid="descuento-mo-aviso">
                      El descuento se reparte en los precios unitarios de mano de obra: el PDF y el Excel del cliente muestran los
                      precios ya descontados y no llevan la línea de descuento. Equivale a {descFinalPct}% del total sin IVA
                      (máximo posible con la mano de obra cargada: {descFinalMax}%). Si después cambian precios o cantidades, volvé a escribir el descuento final.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* V07 P4: Firma configurable del documento PDF — en pestaña Datos del Presupuesto */}
          {!isProyectista && (
            <div className="border border-[#2E6DA4]/30 rounded-xl p-4 bg-blue-50/30">
              <p className="text-xs font-bold text-[#1A4B7A] uppercase tracking-wide mb-3">FIRMA DEL DOCUMENTO</p>
              <p className="text-xs text-gray-500 mb-3">Aparece en el pie de firma del PDF exportado (bloque "Ing. Responsable").</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">NOMBRE:</label>
                  <input
                    data-testid="firma-nombre-input"
                    value={form.firma_nombre}
                    onChange={e => setForm(f => ({ ...f, firma_nombre: e.target.value }))}
                    placeholder="Ej: Géber González"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">CARGO:</label>
                  <input
                    data-testid="firma-cargo-input"
                    value={form.firma_cargo}
                    onChange={e => setForm(f => ({ ...f, firma_cargo: e.target.value }))}
                    placeholder="Ej: Gerencia Técnica"
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pb-2">
            <button onClick={() => setTab("items")}
              className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm">
              <ArrowRight size={15} /> Ir a Estructura de Cómputo
            </button>
          </div>
        </div>
      )}

      {tab === "items" && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* BLOQUE 59 — Barra contextual de selección múltiple (solo visible con ≥1 fila marcada) */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#EBF3FB] border-b border-[#2E6DA4]/20 flex-shrink-0 flex-wrap" data-testid="multi-select-toolbar">
              <CheckSquare size={14} className="text-[#1A4B7A]" />
              <span className="text-xs font-bold text-[#1A4B7A]" data-testid="selected-count-label">
                {selectedIds.size} fila{selectedIds.size !== 1 ? "s" : ""} seleccionada{selectedIds.size !== 1 ? "s" : ""}
              </span>
              {selectionAutoIncluded && (
                <span className="text-[11px] text-[#2E6DA4] italic" data-testid="selection-auto-included-note">
                  · incluye los ítems de la sección seleccionada
                </span>
              )}
              <button
                type="button"
                onClick={() => handleMoveSelection("up")}
                disabled={selectionBounds.atTop}
                className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors border ${selectionBounds.atTop ? "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed" : "text-[#1A4B7A] bg-white border-[#2E6DA4]/30 hover:bg-[#D9EAF8]"}`}
                data-testid="move-selection-up-btn"
                title="Subir el bloque seleccionado"
              >
                <ArrowUp size={12} /> Subir
              </button>
              <button
                type="button"
                onClick={() => handleMoveSelection("down")}
                disabled={selectionBounds.atBottom}
                className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors border ${selectionBounds.atBottom ? "text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed" : "text-[#1A4B7A] bg-white border-[#2E6DA4]/30 hover:bg-[#D9EAF8]"}`}
                data-testid="move-selection-down-btn"
                title="Bajar el bloque seleccionado"
              >
                <ArrowDown size={12} /> Bajar
              </button>
              <button
                type="button"
                onClick={handleDeleteSelected}
                className="flex items-center gap-1 text-xs font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 px-2.5 py-1 rounded-lg transition-colors"
                data-testid="delete-selected-btn"
                title="Eliminar las filas seleccionadas del borrador"
              >
                <Trash2 size={12} /> Eliminar seleccionadas
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="ml-auto flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-white px-2.5 py-1 rounded-lg transition-colors"
                data-testid="clear-selection-btn"
              >
                <X size={12} /> Limpiar selección
              </button>
            </div>
          )}
          <div ref={itemsScrollRef} className="flex-1 overflow-x-scroll overflow-y-auto budget-table-scroll" style={{ scrollbarWidth: "thin", scrollbarColor: "#2E6DA4 #EBF3FB" }} data-testid="items-scroll-container">
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableItemIds} strategy={verticalListSortingStrategy}>
            <table className="w-full text-xs" style={{ minWidth: isProyectista ? "560px" : "1820px", tableLayout: "fixed" }}>
              {/* PDF REV2 #5 + REV3 #7/#8/#9/#10 + BLOQUE 59 (col. selección) — colgroup con drag handle + columnas más anchas */}
              <colgroup>
                <col style={{ width: "32px" }} />
                <col style={{ width: "24px" }} />
                <col style={{ width: `${colWidths.num}px` }} />
                <col style={{ width: `${colWidths.desc}px` }} />
                <col style={{ width: "72px" }} />
                <col style={{ width: `${colWidths.cant}px` }} />
                {!isProyectista && Array.from({ length: 15 }).map((_, i) => (
                  <col key={i} style={{ width: "112px" }} />
                ))}
                <col style={{ width: "40px" }} />
              </colgroup>
              <thead className="bg-[#2E6DA4] text-white sticky top-0 z-10">
                <tr>
                  <th className="px-1 py-2.5 text-center font-semibold tracking-wide" style={{ fontSize: "0.65rem" }} title="Arrastrar para reordenar">⋮⋮</th>
                  <th className="px-1 py-2.5 text-center font-semibold tracking-wide" style={{ fontSize: "0.65rem" }} title="Seleccionar filas"></th>
                  {(isProyectista
                    ? [["N°", "num"], ["DESCRIPCIÓN", "desc"], ["Un.", null], ["Cant.", "cant"], ["", null]]
                    : [["N°", "num"], ["DESCRIPCIÓN", "desc"], ["Un.", null], ["Cant.", "cant"],
                        ["C.U. MAT", null], ["Tot.Mat S/IVA", null], ["Total Mat.", null],
                        ["C.U. M.O", null], ["Tot.MO S/IVA", null], ["Total M.O.", null],
                        ["Costo Dir.", null], ["Tot.G.M", null], ["Tot.G.A", null], ["Tot.G.MO", null],
                        ["P.UNIT.MAT", null], ["P.UNIT.MO", null], ["P.UNITARIO", null],
                        ["IVA (10%)", null], ["PRECIO TOTAL", null], ["", null]]
                  ).map(([h, resizeKey], i) => (
                    <th key={i}
                      className="relative px-2 py-2.5 text-center font-semibold tracking-wide whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ fontSize: "0.65rem" }}
                      title={h}>
                      {h}
                      {resizeKey && (
                        <span
                          onMouseDown={(e) => startResize(resizeKey, e)}
                          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-white/30 active:bg-white/50 transition-colors"
                          style={{ userSelect: "none" }}
                          data-testid={`col-resize-${resizeKey}`}
                          title="Arrastrá para ajustar el ancho de la columna"
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {virtualPaddingTop > 0 && (
                  <tr data-testid="items-virtual-padding-top">
                    <td colSpan={itemsTableColCount} style={{ height: `${virtualPaddingTop}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
                {virtualRows.map((vRow) => {
                  const idx = vRow.index;
                  const item = items[idx];
                  return (
                    <ItemRow key={item.__id || idx} item={item} idx={idx} pct={pct}
                      dragId={item.__id || `row-${idx}`}
                      dragEnabled={true}
                      displayNum={itemNumbers[idx]}
                      groupPct={groupPctByIdx[idx]}
                      groupMatTotal={groupMatTotalByIdx[idx]}
                      precioInputMode={precioInputMode}
                      onChange={updateItem} onRemove={removeItem}
                      onSearch={handleSearch} suggestions={suggestions}
                      showSugg={activeIdx === idx && suggestions.length > 0}
                      onSuggFocus={setActiveIdx}
                      onSuggBlur={onSuggBlurCb}
                      hidePrice={isProyectista}
                      selected={selectedIds.has(item.__id)}
                      onToggleSelect={toggleSelect} />
                  );
                })}
                {virtualPaddingBottom > 0 && (
                  <tr data-testid="items-virtual-padding-bottom">
                    <td colSpan={itemsTableColCount} style={{ height: `${virtualPaddingBottom}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
              </tbody>
            </table>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
            <button onClick={addItem} className="flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] hover:bg-[#EBF3FB] px-3 py-1.5 rounded-lg transition-colors" data-testid="add-item-btn">
              <Plus size={13} /> Agregar ítem
            </button>
            <button onClick={addGroup} className="flex items-center gap-1.5 text-xs font-semibold text-[#1A4B7A] hover:bg-[#D9EAF8] px-3 py-1.5 rounded-lg transition-colors">
              <Plus size={13} /> Agregar sección
            </button>
            <button
              onClick={addSubGroup}
              disabled={!hasMainGroup}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors border ${hasMainGroup ? "text-[#1A4B7A] border-gray-300 hover:bg-gray-100 cursor-pointer" : "text-gray-400 border-gray-200 opacity-50 cursor-not-allowed"}`}
              data-testid="add-subgroup-btn"
              title={hasMainGroup ? "Agrega una sub-sección (ej: PLANTA BAJA) dentro de una sección" : "Primero agregá una sección (nivel 0) para poder anidar una sub-sección"}
            >
              <Plus size={13} /> Agregar sub-sección
            </button>
            <button
              onClick={addSubSubGroup}
              disabled={!hasSubGroup}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors border ${hasSubGroup ? "text-[#1A4B7A] border-gray-300 hover:bg-gray-100 cursor-pointer" : "text-gray-400 border-gray-200 opacity-50 cursor-not-allowed"}`}
              data-testid="add-subsubgroup-btn"
              title={hasSubGroup ? "Agrega una sub-sub-sección (ej: ILUMINACION) dentro de una sub-sección" : "Primero agregá una sub-sección (nivel 1) para poder anidar una sub-sub-sección"}
            >
              <Plus size={13} /> Agregar sub-sub-sección
            </button>
            <button onClick={addMatMenores} className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded-lg transition-colors border border-amber-200" data-testid="add-mat-menores-btn" title="Agrega un ítem cuyo costo se calcula como % del subtotal de materiales del grupo">
              <Plus size={13} /> Mat. menores (% del grupo)
            </button>
            {!isProyectista && budget?._id && (
              <button
                onClick={() => setShowZeroMatModal(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 px-3 py-1.5 rounded-lg transition-colors border border-orange-300"
                data-testid="bulk-zero-mat-btn"
                title="Pone en 0 el costo unitario de materiales de TODOS los ítems (presupuesto solo de Mano de Obra)"
              >
                <Trash2 size={13} /> Limpiar Materiales (C.U.MAT = 0)
              </button>
            )}
            {!isProyectista && (
              <div className="ml-auto flex items-center gap-2">
                <input
                  ref={computoFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportComputo}
                  className="hidden"
                  data-testid="import-computo-input"
                />
                <input
                  ref={computoIaFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportComputoIa}
                  className="hidden"
                  data-testid="import-computo-ia-input"
                />
                {/* Fix ET (2026-10-01): agregar un Excel al final sin tocar lo cargado */}
                <input
                  ref={agregarFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleAgregarComputo}
                  className="hidden"
                  data-testid="agregar-computo-input"
                />
                <button
                  onClick={renumerarSecciones}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors"
                  data-testid="renumerar-secciones-btn"
                  title="Numera de nuevo todas las secciones según el orden actual de la planilla"
                >
                  <RefreshCw size={13} /> Renumerar secciones
                </button>
                <button
                  onClick={() => agregarFileRef.current?.click()}
                  disabled={agregando}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] bg-[#EBF2F9] hover:bg-[#D9EAF8] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                  data-testid="agregar-computo-btn"
                  title="Agrega las secciones e ítems de un Excel al final del presupuesto, sin borrar ni reemplazar lo ya cargado"
                >
                  {agregando ? <Loader2 size={13} className="animate-spin" /> : <FilePlus size={13} />}
                  {agregando ? "Agregando..." : "Agregar Excel"}
                </button>
                <button
                  onClick={() => computoFileRef.current?.click()}
                  disabled={importing}
                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                  data-testid="import-computo-btn"
                  title="Importar cómputo básico (requiere encabezados N°/Descripción/Cantidad)"
                >
                  {importing ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />}
                  {importing ? "Importando..." : "Excel Básico"}
                </button>
                <button
                  onClick={() => computoIaFileRef.current?.click()}
                  disabled={importingIa}
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60 shadow-sm"
                  data-testid="import-computo-ia-btn"
                  title="Importar cómputo con IA (Gemini analiza cualquier formato de Excel)"
                >
                  {importingIa ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {importingIa ? "Analizando con IA..." : "Importar con IA"}
                </button>
              </div>
            )}
          </div>

          {!isProyectista && (
            <div className="px-4 py-4 bg-[#1A4B7A] text-white flex items-center justify-end gap-8 flex-shrink-0" data-testid="budget-footer">
              {totals.descuento_activo ? (
                <>
                  <div className="text-right">
                    <p className="text-xs opacity-70">TOTAL</p>
                    <p className="font-bold text-sm tabular-nums">{fmt(Math.round(totals.subtotalNeto))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-red-300 font-semibold">DESCUENTO -{totals.descuento_porcentaje}%</p>
                    <p className="font-bold text-sm text-red-300 tabular-nums">-{fmt(Math.round(totals.descuento_monto))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs opacity-70">TOTAL CON DESCUENTO</p>
                    <p className="font-bold text-sm tabular-nums">{fmt(Math.round(totals.subtotal_con_descuento))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs opacity-70">IVA (10%)</p>
                    <p className="font-bold text-sm tabular-nums">{fmt(Math.round(totals.iva))}</p>
                  </div>
                  <div className="text-right border-l border-white/30 pl-8">
                    <p className="text-xs opacity-70">TOTAL ITEMS - IVA INCLUIDO</p>
                    <p className="font-bold text-xl tabular-nums">{fmt(Math.round(totals.total))}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-right">
                    <p className="text-xs opacity-70">SUBTOTAL NETO</p>
                    <p className="font-bold text-sm">{fmt(Math.round(totals.subtotalNeto))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs opacity-70">IVA (10%)</p>
                    <p className="font-bold text-sm">{fmt(Math.round(totals.iva))}</p>
                  </div>
                  <div className="text-right border-l border-white/30 pl-8">
                    <p className="text-xs opacity-70">TOTAL IVA INCLUIDO</p>
                    <p className="font-bold text-xl">{fmt(Math.round(totals.total))}</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "bocas" && <BocasTab />}

      {tab === "mo" && <ManoObraTab />}

      {tab === "catalogos" && <CatalogosExtTab />}

      {tab === "cargas" && <TablerosTab budgetId={budget?._id} />}

      {/* Financials tab: keep mounted to preserve state — only hide/show */}
      <div className={tab === "financials" ? "" : "hidden"}>
        <FinancialsTab budgetId={budget?._id} items={items} pct={pct} readOnly={false} budget={budget} precioInputMode={precioInputMode} descuento={descuento} />
      </div>

      {tab === "alcance" && (
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide">EL PRESUPUESTO SI INCLUYE:</label>
              <textarea value={form.alcance_incluye} onChange={e => setForm(f => ({ ...f, alcance_incluye: e.target.value }))} rows={8}
                className="w-full border-2 border-emerald-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-300 bg-emerald-50/30 resize-none"
                placeholder="Listar lo que SÍ incluye el presupuesto..." />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide">EL PRESUPUESTO NO INCLUYE:</label>
              <textarea value={form.alcance_no_incluye} onChange={e => setForm(f => ({ ...f, alcance_no_incluye: e.target.value }))} rows={8}
                className="w-full border-2 border-red-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-300 bg-red-50/30 resize-none"
                placeholder="Listar lo que NO incluye el presupuesto..." />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide">OBSERVACIONES TÉCNICAS:</label>
            <textarea value={form.obs_tecnicas} onChange={e => setForm(f => ({ ...f, obs_tecnicas: e.target.value }))} rows={5}
              className="w-full border-2 border-blue-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-300 bg-blue-50/20 resize-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* C.2 — Marcas Cotizadas split por categoría */}
            <MarcasCotizadasSplitField form={form} setForm={setForm} />
            <div>
              <label className="block text-xs font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide">CONDICIÓN DE PAGO:</label>
              <input value={form.condicion_pago} onChange={e => setForm(f => ({ ...f, condicion_pago: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 bg-yellow-50/40" />
            </div>
            {/* Fix FG (2026-10-08): "desde PRESUPUESTO se debe permitir cotizar en USD si es que se
                necesita" (CEN). Los precios se siguen cargando en Gs.; la cotización (PDF) sale en USD
                al tipo de cambio del presupuesto. */}
            <div data-testid="budget-moneda-field">
              <label className="block text-xs font-bold text-[#1A4B7A] mb-2 uppercase tracking-wide">MONEDA DE LA COTIZACIÓN:</label>
              <div className="flex gap-2">
                <select value={form.moneda || "PYG"} data-testid="budget-moneda-select"
                  onChange={e => { const m = e.target.value; setForm(f => ({ ...f, moneda: m, tipo_cambio: m === "USD" ? (f.tipo_cambio || tcHoy?.usd_pyg || null) : f.tipo_cambio })); }}
                  className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                  <option value="PYG">Guaraníes (PYG)</option>
                  <option value="USD">Dólares (USD)</option>
                </select>
                {form.moneda === "USD" && (
                  <input type="number" value={form.tipo_cambio ?? ""} placeholder={tcHoy ? String(tcHoy.usd_pyg) : "Gs. por USD"} data-testid="budget-tc-input"
                    onChange={e => setForm(f => ({ ...f, tipo_cambio: e.target.value === "" ? null : Number(e.target.value) }))}
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm" title="Tipo de cambio (Gs. por USD)" />
                )}
              </div>
              {form.moneda === "USD" && (
                <p className="text-[11px] text-gray-500 mt-1" data-testid="budget-usd-hint">
                  Los precios se cargan en Gs. y el PDF sale en USD a este tipo de cambio{tcHoy ? ` (hoy: ${fmtTC(tcHoy.usd_pyg)})` : ""}.
                  {tcCotiz > 0 && <> Total de la cotización: <strong>{formatUSD2(totals.total / tcCotiz)}</strong></>}
                </p>
              )}
            </div>
            {/* CEN 2.0 FASE 3 — niveles físicos de la obra */}
            <NivelesField form={form} setForm={setForm} />
          </div>
          {/* CEN 2.0 FASE 2 sub-entrega 2 — Tareas/Niveles asistidos por ítem */}
          {budget?._id && (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
              <p className="text-xs font-bold text-[#1A4B7A] uppercase tracking-wide mb-2">Tareas y Niveles por ítem</p>
              <p className="text-xs text-gray-500 mb-3">Asigná el desglose de tareas (fondo de M.O. por tarea) y el nivel físico de cada ítem — se usan en el módulo Obra. Requiere guardar el presupuesto primero.</p>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setShowImportTareas(true)} data-testid="btn-import-tareas-desglose"
                  className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-semibold">
                  <Upload size={13} /> Importar Desglose de Tareas
                </button>
                <button onClick={() => setShowAsignarTareas(true)} data-testid="btn-asignar-tareas"
                  className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                  <ListChecks size={13} /> Asignar Tareas
                </button>
                <button onClick={() => setShowAsignarNiveles(true)} data-testid="btn-asignar-niveles"
                  className="flex items-center gap-1.5 bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                  <Building2 size={13} /> Asignar Niveles
                </button>
                <button onClick={() => setShowAutollenado(true)} data-testid="btn-autollenado-ia"
                  className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                  <Sparkles size={13} /> Autollenado con IA
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between flex-shrink-0 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancelar</button>
          {/* PDF REV3 #4 — Indicador "Borrador guardado hace X min" */}
          {budget?._id && draftIndicator && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full font-semibold" data-testid="draft-saved-indicator">
              <CheckCircle2 size={11} /> {draftIndicator}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* PDF REV3 #5 + #6 — Histórico de Revisiones */}
          {budget?._id && (
            <button
              onClick={() => setShowRevisionsModal(true)}
              className="flex items-center gap-2 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-[#EBF3FB] px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
              data-testid="open-revisions-btn"
              title="Ver / Restaurar revisiones anteriores"
            >
              <History size={15} /> Revisiones ({Number(budget.revision || 0)})
            </button>
          )}

          {/* A.3.c — Botones de transición contextual (según estado actual del presupuesto) */}
          {budget?._id && currentStatus === "cotizacion" && (
            <button
              onClick={() => setShowTransitionConfirm("revision")}
              disabled={saving || savingDraft}
              className="flex items-center gap-1.5 border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
              data-testid="transition-to-revision-btn"
              title="Pasar este presupuesto al estado Revisión"
            >
              <Eye size={14} /> Pasar a Revisión
            </button>
          )}
          {budget?._id && currentStatus === "revision" && (
            <button
              onClick={() => setShowTransitionConfirm("enviado")}
              disabled={saving || savingDraft}
              className="flex items-center gap-1.5 border border-blue-400 text-blue-700 hover:bg-blue-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
              data-testid="transition-to-enviado-btn"
              title="Marcar como enviado al cliente"
            >
              <Send size={14} /> Enviar al Cliente
            </button>
          )}
          {budget?._id && currentStatus === "enviado" && (
            <>
              <button
                onClick={() => { if (isAdicional) setShowAdicionalModal(true); else setShowTransitionConfirm("aprobado"); }}
                disabled={saving || savingDraft}
                className="flex items-center gap-1.5 border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="transition-to-aprobado-btn"
                title="Marcar como aprobado por el cliente"
              >
                <CheckCircle2 size={14} /> Marcar Aprobado
              </button>
              <button
                onClick={() => setShowTransitionConfirm("rechazado")}
                disabled={saving || savingDraft}
                className="flex items-center gap-1.5 border border-red-400 text-red-600 hover:bg-red-50 px-3 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="transition-to-rechazado-btn"
                title="Marcar como rechazado por el cliente"
              >
                <XCircle size={14} /> Rechazar
              </button>
            </>
          )}

          {/* A.1 — Separador visual entre transiciones y botones de guardado */}
          {budget?._id && <div className="h-8 w-px bg-gray-200 mx-1 flex-shrink-0" />}

          {/* A.1 — "Actualizar Presupuesto" → SECUNDARIO, pequeño, con tooltip de advertencia */}
          {budget?._id && (
            <button
              onClick={handleSaveClick}
              disabled={saving || savingDraft}
              className="flex items-center gap-1.5 border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid="save-budget-btn"
              title={`Atención: crea Revisión ${Number(budget?.revision || 0) + 1}. Usá esto solo si terminaste cambios mayores.`}
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {saving ? "Actualizando..." : `Actualizar (Rev ${Number(budget?.revision || 0) + 1})`}
            </button>
          )}

          {/* A.1 — "Guardar Avance" → DOMINANTE (verde, grande), acción frecuente/segura */}
          {budget?._id && (
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft || saving}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-colors shadow-md disabled:opacity-60"
              data-testid="save-draft-btn"
              title="Guarda el progreso sin crear nueva revisión (acción segura)"
            >
              {savingDraft ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {savingDraft ? "Guardando..." : "Guardar Avance"}
            </button>
          )}

          {/* Crear Presupuesto (solo para budgets nuevos sin _id) */}
          {!budget?._id && (
            <button
              onClick={handleSave}
              disabled={saving || savingDraft}
              className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm disabled:opacity-60"
              data-testid="save-budget-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : null}
              {saving ? "Guardando..." : "Crear Presupuesto"}
            </button>
          )}
        </div>
      </div>

      {/* A.1 — Modal de confirmación antes de "Actualizar Presupuesto" (incrementa revisión) */}
      {/* CEN — Modal de confirmación: limpiar materiales (C.U.MAT = 0) */}
      {showZeroMatModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="bulk-zero-mat-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">Limpiar materiales del presupuesto</h3>
                <p className="text-sm text-gray-500 mt-0.5">Útil para presupuestos solo de Mano de Obra</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3 leading-relaxed">
              Esta acción va a poner en <strong>0</strong> el C.U.MAT (costo unitario de materiales) de
              <strong> TODOS los ítems</strong> del presupuesto.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                <p className="text-xs font-bold text-orange-700 mb-1">SE va a poner en 0</p>
                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                  <li>Costo Unitario de Material (todos los ítems)</li>
                  <li>Materiales menores → recalculan a 0</li>
                </ul>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-700 mb-1">NO se toca</p>
                <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                  <li>Costo Unitario de Mano de Obra</li>
                  <li>Cantidades, descripciones, unidades, frentes</li>
                  <li>Márgenes, marcas, alcance, observaciones</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-5 leading-relaxed">
              El subtotal y el total bajarán al valor de la mano de obra y gastos administrativos.
              Para revertir, vas a tener que cargar los precios de materiales otra vez (o usar
              "Cancelar" arriba si todavía no guardaste).
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowZeroMatModal(false)}
                disabled={zeroingMat}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
                data-testid="bulk-zero-mat-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkZeroMat}
                disabled={zeroingMat}
                className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                data-testid="bulk-zero-mat-confirm"
              >
                {zeroingMat ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {zeroingMat ? "Limpiando..." : "Sí, limpiar materiales"}
              </button>
            </div>
          </div>
        </div>
      )}


      {showUpdateConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={18} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">
                  Crear Revisión {Number(budget?.revision || 0) + 1}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">Esta acción es permanente</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
              "Actualizar Presupuesto" incrementará el contador de{" "}
              <strong className="text-gray-800">Rev {budget?.revision || 0}</strong> a{" "}
              <strong className="text-gray-800">Rev {Number(budget?.revision || 0) + 1}</strong>.
              <br />
              <span className="text-emerald-700 font-medium">Usá "Guardar Avance"</span> si solo querés guardar sin crear una nueva revisión.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpdateConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                data-testid="update-confirm-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                data-testid="update-confirm-ok"
              >
                {saving ? "Guardando..." : `Sí, crear Revisión ${Number(budget?.revision || 0) + 1}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A.3.c — Modal de confirmación de transición de estado */}
      {showTransitionConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="font-bold text-gray-900 text-base mb-1">
              {showTransitionConfirm === "rechazado"
                ? "Rechazar presupuesto"
                : `Pasar a: ${({ revision: "Revisión", enviado: "Enviado", aprobado: "Aprobado" })[showTransitionConfirm] || showTransitionConfirm}`}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {showTransitionConfirm === "rechazado"
                ? "Este presupuesto pasará al estado Rechazado. ¿Confirmás?"
                : `¿Confirmás pasar este presupuesto al estado ${({ revision: "Revisión", enviado: "Enviado", aprobado: "Aprobado" })[showTransitionConfirm] || showTransitionConfirm}?`}
            </p>
            <textarea
              value={transitionComment}
              onChange={e => setTransitionComment(e.target.value)}
              placeholder="Comentario opcional..."
              rows={2}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm mb-4 resize-none focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              data-testid="transition-comment-input"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowTransitionConfirm(null); setTransitionComment(""); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                data-testid="transition-confirm-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleTransitionInForm}
                disabled={transitionLoading}
                className={`px-4 py-2 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${
                  showTransitionConfirm === "rechazado" ? "bg-red-500 hover:bg-red-600" : "bg-[#2E6DA4] hover:bg-[#245A8A]"
                }`}
                data-testid="transition-confirm-ok"
              >
                {transitionLoading ? "..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PROMPT 79 — Modal "Vincular Adicional a Obra" */}
      {showAdicionalModal && (
        <AdicionalObraModal
          budgetId={budget?._id}
          budgetTotal={Math.round(totals?.total || budget?.total || 0)}
          clienteId={form?.cliente_id || budget?.cliente_id || ""}
          onClose={() => setShowAdicionalModal(false)}
          onLinked={(data) => {
            setCurrentStatus("aprobado");
            if (typeof onBudgetUpdated === "function") onBudgetUpdated(data);
            onSave();
          }}
        />
      )}

      {/* FIX 1 — Modal de confirmación de cambio manual de estado (X → Y) */}
      {manualStatusTarget && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="manual-status-confirm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="font-bold text-gray-900 text-base mb-1">Cambiar estado manualmente</h3>
            <p className="text-sm text-gray-600 mb-4">
              Vas a cambiar el estado de{" "}
              <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold text-xs">
                {manualStatusLabel(MANUAL_STATUS_OPTIONS.find(s => s.key === currentStatus) ? currentStatus : "recepcion")}
              </span>{" "}
              <ArrowRight size={13} className="inline text-gray-400" />{" "}
              <span className="inline-block px-2 py-0.5 rounded-full bg-[#EBF3FB] text-[#1A4B7A] font-bold text-xs">
                {manualStatusLabel(manualStatusTarget)}
              </span>.
              <br />
              <span className="text-[11px] text-gray-400">Esto no afecta el contador de revisiones.</span>
            </p>
            <textarea
              value={manualStatusReason}
              onChange={e => setManualStatusReason(e.target.value)}
              placeholder="Motivo (opcional)..."
              rows={2}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm mb-4 resize-none focus:ring-2 focus:ring-[#2E6DA4]/30 focus:border-[#2E6DA4]"
              data-testid="manual-status-reason"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setManualStatusTarget(null); setManualStatusReason(""); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                data-testid="manual-status-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleManualStatusConfirm}
                disabled={manualStatusLoading}
                className="px-4 py-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                data-testid="manual-status-ok"
              >
                {manualStatusLoading ? "Cambiando..." : "Confirmar cambio"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportPreviewModal
        open={!!previewData}
        onClose={() => { setPreviewData(null); }}
        onConfirm={confirmIaImport}
        onAppend={confirmIaAgregar}
        data={previewData}
      />

      {/* PDF REV3 #5 + #6 — Modal de histórico de revisiones */}
      <RevisionsModal
        open={showRevisionsModal}
        budgetId={budget?._id}
        currentBudget={budget}
        onClose={() => setShowRevisionsModal(false)}
        onRestored={onSave}
        onRevisionCreated={onSave}
      />

      {/* CEN 2.0 FASE 2 sub-entrega 2 — Tareas/Niveles asistidos por ítem */}
      <ImportTareasDesgloseModal
        open={showImportTareas}
        onClose={() => setShowImportTareas(false)}
        onImported={() => setShowImportTareas(false)}
      />
      <AsignarTareasModal
        open={showAsignarTareas}
        budgetId={budget?._id}
        onClose={() => setShowAsignarTareas(false)}
        onPatchItems={(byUid) => patchItemsField(byUid, "tareas")}
      />
      <AsignarNivelesModal
        open={showAsignarNiveles}
        budgetId={budget?._id}
        onClose={() => setShowAsignarNiveles(false)}
        onPatchItems={(byUid) => patchItemsField(byUid, "nivel_id")}
        onReplaceItems={replaceItemsFromServer}
      />
      <AutollenadoIAModal
        open={showAutollenado}
        budgetId={budget?._id}
        aprobado={budget?.status === "aprobado"}
        onClose={() => setShowAutollenado(false)}
        onApplied={() => {
          axios.get(`${API}/budgets/${budget._id}`, opts).then(({ data }) => replaceItemsFromServer(data.items || []));
        }}
      />
    </div>
    </CatalogoPresupuestoContext.Provider>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────
